/**
 * Secret Management API schemas
 */

import { z } from 'zod';
import {
    CredentialRotationAttemptState,
    type CredentialRotationAttemptState as CredentialRotationAttemptStateType,
    CredentialRotationDisabledReason,
    type CredentialRotationDisabledReason as CredentialRotationDisabledReasonType,
    type CredentialRotationDomFailureEvidence,
    type CredentialRotationDomSuccessEvidence,
    CredentialRotationRecoverySeverity,
    type CredentialRotationRecoverySeverity as CredentialRotationRecoverySeverityType,
    type CredentialRotationAttemptSummary,
    type CredentialRotationBeginRequest,
    type CredentialRotationBeginActiveRecoveryRequiredResponse,
    type CredentialRotationBeginFieldBindingValue,
    type CredentialRotationBeginMissingCredentialResponse,
    type CredentialRotationBeginNotRotatableResponse,
    type CredentialRotationBeginResolvedResponse,
    type CredentialRotationBeginResponse,
    CredentialRotationBeginResult,
    type CredentialRotationBeginResult as CredentialRotationBeginResultType,
    type CredentialRotationBeginStaleProjectionResponse,
    type CredentialRotationBeginUnauthorizedResponse,
    type CredentialRotationFailRequest,
    type CredentialRotationInjectedRequest,
    type CredentialRotationPromoteExpiredAttemptResponse,
    type CredentialRotationPromoteInvalidStateResponse,
    type CredentialRotationPromotePromotionFailedResponse,
    type CredentialRotationPromoteRequest,
    type CredentialRotationPromoteResponse,
    CredentialRotationPromoteResult,
    type CredentialRotationPromoteResult as CredentialRotationPromoteResultType,
    type CredentialRotationPromoteStaleProjectionResponse,
    type CredentialRotationPromoteSucceededResponse,
    type CredentialRotationPromoteUnauthorizedResponse,
    type CredentialRotationSubmittedRequest,
    type CredentialRotationEvidence,
    CredentialRotationEvidenceKind,
    type CredentialRotationEvidenceKind as CredentialRotationEvidenceKindType,
    type CredentialRotationGeneratedFieldMetadata,
    type CredentialRotationGenerationMetadata,
    type CredentialRotationHistoryAttemptCapabilities,
    type CredentialRotationHistoryCapabilities,
    type CredentialRotationHistoryDisplayMetadata,
    type CredentialRotationHistoryListItem,
    type CredentialRotationHistoryRequest,
    type CredentialRotationHistoryResponse,
    type CredentialRotationHistoryTargetFieldDisplay,
    type CredentialRotationInjectionEvidence,
    type CredentialRotationManualPromotionEvidence,
    type CredentialRotationManualPromotionConfirmation,
    type CredentialRotationManualPromotionExpiredAttemptResponse,
    type CredentialRotationManualPromotionInvalidStateResponse,
    type CredentialRotationManualPromotionPromotionFailedResponse,
    type CredentialRotationManualPromotionRequest,
    type CredentialRotationManualPromotionResponse,
    CredentialRotationManualPromotionResult,
    type CredentialRotationManualPromotionResult as CredentialRotationManualPromotionResultType,
    type CredentialRotationManualPromotionStaleTargetResponse,
    type CredentialRotationManualPromotionSucceededResponse,
    type CredentialRotationManualPromotionUnauthorizedResponse,
    type CredentialRotationSubmissionTriggerEvidence,
    CredentialType,
    CredentialOwnerScope,
    IpxCredentialDefinitionKind,
    type ManualCredentialCaptureCreatedResponse,
    type ManualCredentialCaptureInvalidEvidenceResponse,
    type ManualCredentialCapturePersistenceFailedResponse,
    type ManualCredentialCaptureRequest,
    type ManualCredentialCaptureResponse,
    ManualCredentialCaptureResult,
    type ManualCredentialCaptureResult as ManualCredentialCaptureResultType,
    type ManualCredentialCaptureStaleProjectionResponse,
    type ManualCredentialCaptureUnauthorizedResponse,
    type ManualCredentialCaptureUpdatedResponse,
    type PortalCredentialRotationEligibility,
    type PortalCredentialRotationFlowSummary,
    type PortalCredentialRotationRecoverySummary,
    SupportedCredentialSystemIds,
    type AtomicCredentialTypeDefinition,
    type CreateIpxCredentialRequest,
    type CreateOrganizationIpxCredentialPolicyRequest,
    type CreateSecretRequest,
    type CredentialOwnerScope as CredentialOwnerScopeType,
    type DraftGenericCredentials,
    type GenericCredentials,
    type MultiCredentialTypeDefinition,
    type ThirdPartyCredentials,
    type ThirdPartyCredentialsDraft,
    type UniqueCredentialTypeDefinition,
    type SupportedCredentialSystemId,
    type UpdateSecretRequest,
    type ListSecretsForOrganizationQuery as ListSecretsForOrganizationQueryType,
    type CredentialUsageListItem,
    type CredentialUsageReference,
    type CredentialUsageType,
    type CreateSecretResponse,
    type Credentials,
    type DashboardWebsiteCredentialPolicyListItem,
    type DashboardWebsiteCredentialSlot,
    type CreateDashboardWebsiteCredentialRequest,
    type CredentialDeleteConflictResponse,
    type CredentialUsagesResponse,
    type DisconnectCredentialUsagesRequest,
    type DisconnectCredentialUsagesResponse,
    type EligibleIpxCredentialRoleAssignmentListItem,
    type GetSecretResponse,
    type ListSecretsResponse,
    type ListSecretsResponseCapabilities,
    type SaveSecretResponse,
    type SecretIpxCredentialPolicyLinkMetadata,
    type SecretListItemCapabilities,
    type SecretListItemUsageData,
    type SecretListItemUsageSummary,
    type SecretListItem,
    type SecretWithCredentialsResponse,
    type SecretIdentityReference,
    type SecretOrganizationUuidReference,
    type SecretUuidReference,
    type SecretOrganizationReference,
    type ListSecretCredentialDefinitionsQuery,
    type ListSecretCredentialDefinitionsResponse,
    type SecretCredentialDefinitionListItem,
    type IpxCredentialDefinitionActivePolicyMetadata,
    type IpxCredentialDefinitionBaseProjection,
    type IpxMachineAuthCredentialDefinitionProjection,
    type IpxMachineAuthCredentialDefinitionSearchListItem,
    type IpxCredentialDefinitionProjection,
    type IpxCredentialDefinitionSearchBaseListItem,
    type IpxCredentialDefinitionSearchListItem,
    type IpxCredentialDefinitionSearchRequest,
    type IpxCredentialDefinitionSearchResponse,
    type SearchOrganizationIpxCredentialPolicyDefinitionsRequest,
    type SearchOrganizationIpxCredentialPolicyDefinitionsResponse,
    type IpxWebLoginCredentialDefinitionProjection,
    type IpxWebLoginCredentialDefinitionSearchListItem,
    type IpxCredentialMaskedCredentialMetadata,
    type IpxCredentialPolicyCredentialCollectionCapabilities,
    type IpxCredentialRequestFields,
    type IpxCredentialDefinitionIdentity,
    type IpxCredentialCreateCapability,
    type IpxCredentialEditedFields,
    type IpxCredentialSaveFields,
    type IpxCredentialFieldDescriptor,
    type IpxCredentialFieldDisplayConfig,
    type IpxCredentialFieldConfig,
    type IpxCredentialFieldConfigMap,
    type IpxCredentialFieldName,
    IpxCredentialFieldNamePattern,
    type IpxCredentialFieldGenerationPolicy,
    type IpxCredentialFieldValidationPolicy,
    IpxCredentialFieldCharacterClass,
    IpxCredentialFieldClassification,
    IpxCredentialFieldKind,
    type IpxCredentialOwnerMetadata,
    type IpxCredentialRuntimeWebResourceModelProjection,
    type IpxCredentialRuntimeMatchedUrlData,
    type IpxCredentialScope,
    type IpxCredentialSummaryCapabilities,
    type IpxCredentialSummaryListItem,
    IpxCredentialScopeType,
    IpxCredentialRuntimeResolveResult,
    type IpxCredentialRuntimeResolveRequest,
    type IpxCredentialRuntimeMissingCredentialResponse,
    type IpxCredentialRuntimeResolvedResponse,
    type IpxCredentialRuntimeResolveResponse,
    type IpxCredentialRuntimeStaleProjectionResponse,
    type IpxCredentialRuntimeBootstrapRequest,
    type IpxCredentialRuntimeBootstrapResponse,
    type IpxCredentialRuntimeRoleScope,
    type IpxCredentialRuntimeWorkflowProjection,
    type ListIpxCredentialsForPolicyResponse,
    type ListDashboardWebsiteCredentialsRequest,
    type ListDashboardWebsiteCredentialsResponse,
    type ListOrganizationIpxCredentialPoliciesRequest,
    type ListOrganizationIpxCredentialPoliciesResponse,
    type OrganizationIpxCredentialPolicyLifecycleRequest,
    type OrganizationIpxCredentialPolicyCapabilities,
    type OrganizationIpxCredentialPolicyCollectionCapabilities,
    type OrganizationIpxCredentialPolicyListItem,
    type OrganizationIpxCredentialPolicyResponse,
    type RpaBuilderActiveIpxContext,
    type RpaBuilderAttachCredentialPolicyRequest,
    type RpaBuilderAttachCredentialPolicyResponse,
    type RpaBuilderBootstrapRequest,
    type RpaBuilderBootstrapResponse,
    type RpaBuilderCapabilityFlags,
    type RpaBuilderConfigureLoginWorkflowCredentialDefinitionCreateRequest,
    type RpaBuilderConfigureLoginWorkflowCredentialDefinitionRequest,
    type RpaBuilderConfigureLoginWorkflowCredentialDefinitionUpdateRequest,
    type RpaBuilderConfigureLoginWorkflowSaveRequest,
    type RpaBuilderConfigureLoginWorkflowSaveResponse,
    type RpaBuilderConfigureLoginWorkflowWebResourceModelCreateRequest,
    type RpaBuilderConfigureLoginWorkflowWebResourceModelReplaceRequest,
    type RpaBuilderConfigureLoginWorkflowWebResourceModelRequest,
    type RpaBuilderCredentialPolicyMutationRequest,
    type RpaBuilderCredentialPolicyMutationResponse,
    type RpaBuilderCreateCredentialDefinitionRequest,
    type RpaBuilderCredentialDefinitionListItem,
    type RpaBuilderCredentialDefinitionProjection,
    type RpaBuilderCredentialDefinitionSharedMetadata,
    type RpaBuilderDeleteCredentialDefinitionRequest,
    type RpaBuilderDeleteCredentialDefinitionResponse,
    type RpaBuilderCreateWebResourceModelRequest,
    type RpaBuilderDeleteWebResourceModelRequest,
    type RpaBuilderDeleteWebResourceModelResponse,
    type RpaBuilderDeactivateCredentialPolicyRequest,
    type RpaBuilderDeactivateCredentialPolicyResponse,
    type RpaBuilderFetchWebResourceModelRequest,
    type RpaBuilderFetchWebResourceModelResponse,
    type RpaBuilderListCredentialDefinitionsRequest,
    type RpaBuilderListCredentialDefinitionsResponse,
    type RpaBuilderListWebResourceModelsRequest,
    type RpaBuilderListWebResourceModelsResponse,
    RpaBuilderInvalidWebResourceModelReason,
    type RpaBuilderInvalidWebResourceModelReason as RpaBuilderInvalidWebResourceModelReasonType,
    type RpaBuilderReactivateCredentialPolicyRequest,
    type RpaBuilderReactivateCredentialPolicyResponse,
    type RpaBuilderReplaceWebResourceModelRequest,
    type RpaBuilderSaveCredentialDefinitionFields,
    type RpaBuilderSaveCredentialDefinitionResponse,
    type RpaBuilderSaveWebResourceModelFields,
    type RpaBuilderSaveWebResourceModelResponse,
    type RpaBuilderUpdateCredentialDefinitionRequest,
    type RpaBuilderWebResourceModelIdentity,
    type RpaBuilderInvalidWebResourceModelMetadata,
    type RpaBuilderInvalidWebResourceModelProjection,
    type RpaBuilderValidWebResourceModelProjection,
    type RpaBuilderWebResourceModelProjection,
    type RevealIpxCredentialSecretFieldRequest,
    type RevealIpxCredentialSecretFieldResponse,
    type UpdateIpxCredentialRequest,
} from '../secret.types';
import {
    DraftValorCredentialsSchema,
    ValorCredentialsSchema,
} from './ipg/valor/valor-credential.schemas';
import { UrlPatternStringArraySchema, UrlPatternStringSchema } from '../backend_config.types';
import { NonEmptyStringSchema, PositiveIntSchema } from './common.schemas';
import {
    AuthorizationOpaqueCursorQuerySchema,
    AuthorizationPaginationLimitQuerySchema,
} from './authorization.schemas';
import {
    IpxFlowFieldBindingSourceSchema,
    IpxFlowOutcomePredicateSchema,
    IpxLoginWorkflowStepListSchema,
    IpxManualCaptureTriggerKindSchema,
    IpxWebResourceModelDocumentSchema,
} from './samba-rpa-workflow.schemas';
import type { AuthorizableAction } from '../authorization-contracts';
import { ALL_AUTHORIZABLE_ACTIONS } from '../authorization-contracts';
import { Role, Status } from '@pillar-portal/database/dist/enums';
import { IncludeDescendantsQuerySchema, SearchTermsSchema } from './omnisearch.schemas';
import { OpaqueCursorPaginationSchema } from '../authorization.types';
import { GenericThirdPartySystemId } from '../types/transaction-details.enums';
import { ValorThirdPartySystemId } from '../types/ipg/valor/valor-system-constants';

/**
 * Generic website credentials public fields
 */
export const GenericCredentialsPublicSchema: z.ZodObject<{
    username: z.ZodString;
}> = z.object({
    username: z.string().min(1),
});

/**
 * Generic website credentials secret fields
 */
export const GenericCredentialsSecretSchema: z.ZodObject<{
    password: z.ZodString;
}> = z.object({
    password: z.string().min(1),
});

/**
 * Generic website credentials structure
 */
export const GenericCredentialsSchema: z.ZodType<GenericCredentials> =
    GenericCredentialsPublicSchema.merge(GenericCredentialsSecretSchema);

export const DraftGenericCredentialsSchema: z.ZodType<DraftGenericCredentials> = z
    .object({
        username: z.string().min(1).optional(),
        password: z.string().min(1).optional(),
    })
    .refine((value) => Object.keys(value).length > 0, {
        message: 'At least one credential field is required',
    });

export const ThirdPartyCredentialsSchema: z.ZodType<ThirdPartyCredentials> = z.union([
    GenericCredentialsSchema,
    ValorCredentialsSchema,
]);

export const ThirdPartyCredentialsDraftSchema: z.ZodType<ThirdPartyCredentialsDraft> = z.union([
    DraftGenericCredentialsSchema,
    DraftValorCredentialsSchema,
]);

export const ExplicitCredentialsSchemaBySystemId: Partial<
    Record<SupportedCredentialSystemId, z.ZodType<ThirdPartyCredentials>>
> = {
    [ValorThirdPartySystemId]: ValorCredentialsSchema,
};

export const ExplicitDraftCredentialsSchemaBySystemId: Partial<
    Record<SupportedCredentialSystemId, z.ZodType<ThirdPartyCredentialsDraft>>
> = {
    [ValorThirdPartySystemId]: DraftValorCredentialsSchema,
};

export function getCredentialsSchemaForSystem(
    systemId: SupportedCredentialSystemId
): z.ZodType<ThirdPartyCredentials> {
    return ExplicitCredentialsSchemaBySystemId[systemId] ?? GenericCredentialsSchema;
}

export function getDraftCredentialsSchemaForSystem(
    systemId: SupportedCredentialSystemId
): z.ZodType<ThirdPartyCredentialsDraft> {
    return ExplicitDraftCredentialsSchemaBySystemId[systemId] ?? DraftGenericCredentialsSchema;
}

export function isSupportedCredentialSystemId(value: number): value is SupportedCredentialSystemId {
    return SupportedCredentialSystemIds.some((systemId) => systemId === value);
}

export const SupportedCredentialSystemIdSchema: z.ZodType<SupportedCredentialSystemId> = z
    .number()
    .refine((value): value is SupportedCredentialSystemId => isSupportedCredentialSystemId(value));

export const SupportedCredentialSystemIdsQuerySchema: z.ZodType<
    SupportedCredentialSystemId[] | undefined
> = z
    .union([
        z.array(z.coerce.number()),
        z.coerce.number().transform((value: number): number[] => [value]),
    ])
    .optional()
    .transform((values: number[] | undefined): SupportedCredentialSystemId[] | undefined => {
        if (values === undefined) {
            return undefined;
        }
        return values.map((value: number) => SupportedCredentialSystemIdSchema.parse(value));
    });

export const BooleanQuerySchema: z.ZodType<boolean | undefined> = z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .transform((value: boolean | 'true' | 'false'): boolean => value === true || value === 'true')
    .optional();

export const CredentialOwnerScopeSchema: z.ZodType<CredentialOwnerScopeType> = z.enum([
    CredentialOwnerScope.ROLE_ASSIGNMENT,
    CredentialOwnerScope.ORGANIZATION,
]);

export const AtomicCredentialTypeDefinitionSchema: z.ZodType<AtomicCredentialTypeDefinition> =
    z.object({
        name: z.string().min(1),
        publicFields: z.array(z.string().min(1)),
        secretFields: z.array(z.string().min(1)),
        requiredFields: z.array(z.string().min(1)),
        credentialOwnerScope: CredentialOwnerScopeSchema,
    });

export const UniqueCredentialTypeDefinitionSchema: z.ZodType<UniqueCredentialTypeDefinition> =
    z.object({
        kind: z.literal('unique'),
        fields: AtomicCredentialTypeDefinitionSchema,
    });

export const MultiCredentialTypeDefinitionSchema: z.ZodType<MultiCredentialTypeDefinition> =
    z.object({
        kind: z.literal('multi'),
        definitions: z.record(z.string().min(1), AtomicCredentialTypeDefinitionSchema),
    });

export const IpxCredentialDefinitionActivePolicyMetadataSchema: z.ZodType<IpxCredentialDefinitionActivePolicyMetadata> =
    z.object({
        organization_ipx_credential_policy_uuid: z.string().uuid(),
        status: z.enum(Status),
    });

export const IpxWebResourceModelDigestSchema: z.ZodType<string> = z
    .string()
    .regex(/^[a-f0-9]{64}$/);

export const IpxCredentialRuntimeWebResourceModelProjectionSchema: z.ZodType<IpxCredentialRuntimeWebResourceModelProjection> =
    z.object({
        ipx_web_resource_model_uuid: z.string().uuid(),
        web_resource_model: IpxWebResourceModelDocumentSchema,
        model_digest: IpxWebResourceModelDigestSchema,
    });

export const IpxCredentialDefinitionBaseProjectionSchema: z.ZodType<IpxCredentialDefinitionBaseProjection> =
    z
        .object({
            ipx_credential_definition_uuid: z.string().uuid(),
            system_id: SupportedCredentialSystemIdSchema,
            credential_definition_key: z.string(),
            credential_definition_kind: z.enum(IpxCredentialDefinitionKind),
            status: z.enum(Status),
            credential_owner_scope: z.enum(CredentialOwnerScope),
            credential_field_config: z.lazy(() => IpxCredentialFieldConfigMapSchema),
            active_policy:
                IpxCredentialDefinitionActivePolicyMetadataSchema.nullable(),
        })
        .strict();

export const IpxWebLoginCredentialDefinitionProjectionSchema: z.ZodType<IpxWebLoginCredentialDefinitionProjection> =
    z
        .object({
            ipx_credential_definition_uuid: z.string().uuid(),
            system_id: SupportedCredentialSystemIdSchema,
            credential_definition_key: z.string(),
            credential_definition_kind: z.literal(
                IpxCredentialDefinitionKind.WEB_LOGIN_WORKFLOW
            ),
            status: z.enum(Status),
            credential_owner_scope: z.enum(CredentialOwnerScope),
            credential_field_config: z.lazy(() => IpxCredentialFieldConfigMapSchema),
            active_policy:
                IpxCredentialDefinitionActivePolicyMetadataSchema.nullable(),
            trigger_url_pattern: UrlPatternStringArraySchema,
            login_workflow: IpxLoginWorkflowStepListSchema,
            ipx_web_resource_model:
                IpxCredentialRuntimeWebResourceModelProjectionSchema.nullable().optional(),
        })
        .strict();

export const IpxMachineAuthCredentialDefinitionProjectionSchema: z.ZodType<IpxMachineAuthCredentialDefinitionProjection> =
    z
        .object({
            ipx_credential_definition_uuid: z.string().uuid(),
            system_id: SupportedCredentialSystemIdSchema,
            credential_definition_key: z.string(),
            credential_definition_kind: z.literal(
                IpxCredentialDefinitionKind.MACHINE_AUTH
            ),
            status: z.enum(Status),
            credential_owner_scope: z.enum(CredentialOwnerScope),
            credential_field_config: z.lazy(() => IpxCredentialFieldConfigMapSchema),
            active_policy:
                IpxCredentialDefinitionActivePolicyMetadataSchema.nullable(),
            trigger_url_pattern: z.null(),
            login_workflow: z.null(),
            ipx_web_resource_model: z.null().optional(),
        })
        .strict();

export const IpxCredentialDefinitionProjectionSchema: z.ZodType<IpxCredentialDefinitionProjection> =
    z.union([
        IpxWebLoginCredentialDefinitionProjectionSchema,
        IpxMachineAuthCredentialDefinitionProjectionSchema,
    ]);

export const IpxCredentialDefinitionSearchRequestSchema: z.ZodType<IpxCredentialDefinitionSearchRequest> =
    z
        .object({
            terms: SearchTermsSchema,
            limit: AuthorizationPaginationLimitQuerySchema,
            cursor: AuthorizationOpaqueCursorQuerySchema,
            status: z.enum(Status).optional(),
            ipx_system_id: SupportedCredentialSystemIdSchema.optional(),
        })
        .strict();

export const IpxCredentialDefinitionSearchBaseListItemSchema: z.ZodType<IpxCredentialDefinitionSearchBaseListItem> =
    z
        .object({
            ipx_credential_definition_uuid: z.string().uuid(),
            name: NonEmptyStringSchema,
            system_id: SupportedCredentialSystemIdSchema,
            credential_definition_key: z.string(),
            credential_definition_kind: z.enum(IpxCredentialDefinitionKind),
            status: z.enum(Status),
            credential_owner_scope: z.enum(CredentialOwnerScope),
            credential_field_config: z.lazy(() => IpxCredentialFieldConfigMapSchema),
            credential_type_display_name: NonEmptyStringSchema,
            public_field_names: z.array(NonEmptyStringSchema),
            secret_field_names: z.array(NonEmptyStringSchema),
            required_field_names: z.array(NonEmptyStringSchema),
        })
        .strict();

export const IpxWebLoginCredentialDefinitionSearchListItemSchema: z.ZodType<IpxWebLoginCredentialDefinitionSearchListItem> =
    z
        .object({
            ipx_credential_definition_uuid: z.string().uuid(),
            name: NonEmptyStringSchema,
            system_id: SupportedCredentialSystemIdSchema,
            credential_definition_key: z.string(),
            credential_definition_kind: z.literal(
                IpxCredentialDefinitionKind.WEB_LOGIN_WORKFLOW
            ),
            status: z.enum(Status),
            credential_owner_scope: z.enum(CredentialOwnerScope),
            credential_field_config: z.lazy(() => IpxCredentialFieldConfigMapSchema),
            credential_type_display_name: NonEmptyStringSchema,
            public_field_names: z.array(NonEmptyStringSchema),
            secret_field_names: z.array(NonEmptyStringSchema),
            required_field_names: z.array(NonEmptyStringSchema),
            trigger_url_pattern: UrlPatternStringArraySchema,
            login_workflow: IpxLoginWorkflowStepListSchema,
            ipx_web_resource_model_uuid: z.string().uuid().nullable().optional(),
        })
        .strict();

export const IpxMachineAuthCredentialDefinitionSearchListItemSchema: z.ZodType<IpxMachineAuthCredentialDefinitionSearchListItem> =
    z
        .object({
            ipx_credential_definition_uuid: z.string().uuid(),
            name: NonEmptyStringSchema,
            system_id: SupportedCredentialSystemIdSchema,
            credential_definition_key: z.string(),
            credential_definition_kind: z.literal(
                IpxCredentialDefinitionKind.MACHINE_AUTH
            ),
            status: z.enum(Status),
            credential_owner_scope: z.enum(CredentialOwnerScope),
            credential_field_config: z.lazy(() => IpxCredentialFieldConfigMapSchema),
            credential_type_display_name: NonEmptyStringSchema,
            public_field_names: z.array(NonEmptyStringSchema),
            secret_field_names: z.array(NonEmptyStringSchema),
            required_field_names: z.array(NonEmptyStringSchema),
            trigger_url_pattern: z.null(),
            login_workflow: z.null(),
            ipx_web_resource_model_uuid: z.null().optional(),
        })
        .strict();

export const IpxCredentialDefinitionSearchListItemSchema: z.ZodType<IpxCredentialDefinitionSearchListItem> =
    z.union([
        IpxWebLoginCredentialDefinitionSearchListItemSchema,
        IpxMachineAuthCredentialDefinitionSearchListItemSchema,
    ]);

export const IpxCredentialDefinitionSearchResponseSchema: z.ZodType<IpxCredentialDefinitionSearchResponse> =
    z
        .object({
            definitions: z.array(IpxCredentialDefinitionSearchListItemSchema),
            pagination: OpaqueCursorPaginationSchema,
        })
        .strict();

export const SearchOrganizationIpxCredentialPolicyDefinitionsRequestSchema: z.ZodType<SearchOrganizationIpxCredentialPolicyDefinitionsRequest> =
    z
        .object({
            terms: SearchTermsSchema,
            limit: AuthorizationPaginationLimitQuerySchema,
            cursor: AuthorizationOpaqueCursorQuerySchema,
            status: z.enum(Status).optional(),
            ipx_system_id: SupportedCredentialSystemIdSchema.optional(),
            organization_uuid: z.string().uuid(),
        })
        .strict();

export const SearchOrganizationIpxCredentialPolicyDefinitionsResponseSchema: z.ZodType<SearchOrganizationIpxCredentialPolicyDefinitionsResponse> =
    z
        .object({
            definitions: z.array(
                IpxWebLoginCredentialDefinitionSearchListItemSchema
            ),
            pagination: OpaqueCursorPaginationSchema,
        })
        .strict();

export const CredentialRotationAttemptStateSchema: z.ZodType<CredentialRotationAttemptStateType> =
    z.enum(CredentialRotationAttemptState);

export const CredentialRotationDisabledReasonSchema: z.ZodType<CredentialRotationDisabledReasonType> =
    z.enum(CredentialRotationDisabledReason);

export const CredentialRotationRecoverySeveritySchema: z.ZodType<CredentialRotationRecoverySeverityType> =
    z.enum(CredentialRotationRecoverySeverity);

export const OrganizationIpxCredentialPolicyCollectionCapabilitiesSchema: z.ZodType<OrganizationIpxCredentialPolicyCollectionCapabilities> =
    z
        .object({
            can_create: z.boolean(),
        })
        .strict();

export const OrganizationIpxCredentialPolicyCapabilitiesSchema: z.ZodType<OrganizationIpxCredentialPolicyCapabilities> =
    z
        .object({
            can_view: z.boolean(),
            can_disable: z.boolean(),
            can_enable: z.boolean(),
            can_delete: z.boolean(),
            can_restore: z.boolean(),
        })
        .strict();

export const OrganizationIpxCredentialPolicyListItemSchema: z.ZodType<OrganizationIpxCredentialPolicyListItem> =
    z
        .object({
            organization_ipx_credential_policy_uuid: z.string().uuid(),
            organization_uuid: z.string().uuid(),
            ipx_credential_definition_uuid: z.string().uuid(),
            ipx_credential_definition_name: NonEmptyStringSchema,
            default_account_id: NonEmptyStringSchema,
            system_id: SupportedCredentialSystemIdSchema,
            credential_definition_key: z.string(),
            status: z.enum(Status),
            credential_count: z.number().int().nonnegative(),
            missing_personal_credential_count: z.number().int().nonnegative(),
            capabilities: OrganizationIpxCredentialPolicyCapabilitiesSchema,
        })
        .strict();

export const ListOrganizationIpxCredentialPoliciesRequestSchema: z.ZodType<ListOrganizationIpxCredentialPoliciesRequest> =
    z
        .object({
            organization_uuid: z.string().uuid(),
            status: z.enum(Status).optional(),
            limit: AuthorizationPaginationLimitQuerySchema,
            cursor: AuthorizationOpaqueCursorQuerySchema,
        })
        .strict();

export const ListOrganizationIpxCredentialPoliciesResponseSchema: z.ZodType<ListOrganizationIpxCredentialPoliciesResponse> =
    z
        .object({
            capabilities:
                OrganizationIpxCredentialPolicyCollectionCapabilitiesSchema,
            policies: z.array(OrganizationIpxCredentialPolicyListItemSchema),
            pagination: OpaqueCursorPaginationSchema.optional(),
        })
        .strict();

export const ListDashboardWebsiteCredentialsRequestSchema: z.ZodType<ListDashboardWebsiteCredentialsRequest> =
    z
        .object({
            organization_uuid: z.string().uuid(),
        })
        .strict();

export const CreateOrganizationIpxCredentialPolicyRequestSchema: z.ZodType<CreateOrganizationIpxCredentialPolicyRequest> =
    z
        .object({
            organization_uuid: z.string().uuid(),
            ipx_credential_definition_uuid: z.string().uuid(),
        })
        .strict();

export const OrganizationIpxCredentialPolicyLifecycleRequestSchema: z.ZodType<OrganizationIpxCredentialPolicyLifecycleRequest> =
    z
        .object({
            organization_uuid: z.string().uuid(),
            organization_ipx_credential_policy_uuid: z.string().uuid(),
        })
        .strict();

export const OrganizationIpxCredentialPolicyResponseSchema: z.ZodType<OrganizationIpxCredentialPolicyResponse> =
    z
        .object({
            policy: OrganizationIpxCredentialPolicyListItemSchema,
        })
        .strict();

const OrganizationIpxCredentialScopeSchemaBase = z
    .object({
        scope_type: z.literal(IpxCredentialScopeType.ORGANIZATION),
    })
    .strict();

const RoleAssignmentIpxCredentialScopeSchemaBase = z
    .object({
        scope_type: z.literal(IpxCredentialScopeType.ROLE_ASSIGNMENT),
        role_assignment_uuid: z.string().uuid(),
    })
    .strict();

export const IpxCredentialScopeSchema: z.ZodType<IpxCredentialScope> =
    z.discriminatedUnion('scope_type', [
        OrganizationIpxCredentialScopeSchemaBase,
        RoleAssignmentIpxCredentialScopeSchemaBase,
    ]);

export const IpxCredentialFieldNameSchema: z.ZodType<IpxCredentialFieldName> =
    z
        .string()
        .min(1)
        .max(64)
        .regex(IpxCredentialFieldNamePattern);

export const CredentialRotationEvidenceKindSchema: z.ZodType<CredentialRotationEvidenceKindType> =
    z.enum(CredentialRotationEvidenceKind);

export const CredentialRotationDomSuccessEvidenceSchema: z.ZodType<CredentialRotationDomSuccessEvidence> =
    z
        .object({
            kind: z.literal(CredentialRotationEvidenceKind.DOM_SUCCESS),
            flow_key: NonEmptyStringSchema,
            screen_key: NonEmptyStringSchema,
            predicate: IpxFlowOutcomePredicateSchema,
            observed_ts: z.string().datetime(),
        })
        .strict();

export const CredentialRotationDomFailureEvidenceSchema: z.ZodType<CredentialRotationDomFailureEvidence> =
    z
        .object({
            kind: z.literal(CredentialRotationEvidenceKind.DOM_FAILURE),
            flow_key: NonEmptyStringSchema,
            screen_key: NonEmptyStringSchema,
            predicate: IpxFlowOutcomePredicateSchema,
            observed_ts: z.string().datetime(),
        })
        .strict();

export const CredentialRotationSubmissionTriggerEvidenceSchema: z.ZodType<CredentialRotationSubmissionTriggerEvidence> =
    z
        .object({
            kind: z.literal(CredentialRotationEvidenceKind.SUBMISSION_TRIGGER),
            flow_key: NonEmptyStringSchema,
            screen_key: NonEmptyStringSchema,
            trigger_kind: IpxManualCaptureTriggerKindSchema,
            observed_ts: z.string().datetime(),
            request_url: NonEmptyStringSchema.optional(),
        })
        .strict();

export const CredentialRotationInjectionEvidenceSchema: z.ZodType<CredentialRotationInjectionEvidence> =
    z
        .object({
            kind: z.literal(CredentialRotationEvidenceKind.INJECTION),
            flow_key: NonEmptyStringSchema,
            screen_key: NonEmptyStringSchema,
            field_names: z.array(IpxCredentialFieldNameSchema).min(1),
            observed_ts: z.string().datetime(),
        })
        .strict();

export const CredentialRotationManualPromotionEvidenceSchema: z.ZodType<CredentialRotationManualPromotionEvidence> =
    z
        .object({
            kind: z.literal(CredentialRotationEvidenceKind.MANUAL_PROMOTION),
            actor_role_assignment_uuid: z.string().uuid(),
            confirmed_ts: z.string().datetime(),
            note: NonEmptyStringSchema.optional(),
        })
        .strict();

export const CredentialRotationEvidenceSchema: z.ZodType<CredentialRotationEvidence> =
    z.union([
        CredentialRotationDomSuccessEvidenceSchema,
        CredentialRotationDomFailureEvidenceSchema,
        CredentialRotationSubmissionTriggerEvidenceSchema,
        CredentialRotationInjectionEvidenceSchema,
        CredentialRotationManualPromotionEvidenceSchema,
    ]);

export const IpxCredentialFieldCharacterClassSchema: z.ZodType<IpxCredentialFieldCharacterClass> =
    z.enum(IpxCredentialFieldCharacterClass);

export const CredentialRotationGeneratedFieldMetadataSchema: z.ZodType<CredentialRotationGeneratedFieldMetadata> =
    z
        .object({
            field_name: IpxCredentialFieldNameSchema,
            length: z.number().int().nonnegative(),
            required_classes: z.array(IpxCredentialFieldCharacterClassSchema),
        })
        .strict();

export const CredentialRotationGenerationMetadataSchema: z.ZodType<CredentialRotationGenerationMetadata> =
    z
        .object({
            policy_version: z.number().int().positive(),
            field_names: z.array(IpxCredentialFieldNameSchema).min(1),
            fields: z.record(
                IpxCredentialFieldNameSchema,
                CredentialRotationGeneratedFieldMetadataSchema
            ),
            generated_ts: z.string().datetime(),
        })
        .strict()
        .superRefine((metadata, ctx) => {
            for (const fieldName of metadata.field_names) {
                if (metadata.fields[fieldName] === undefined) {
                    ctx.addIssue({
                        code: 'custom',
                        message:
                            'generation metadata field_names must exist in fields',
                        path: ['field_names'],
                    });
                }
            }

            for (const fieldName of Object.keys(metadata.fields)) {
                const fieldMetadata = metadata.fields[fieldName];
                if (fieldMetadata === undefined) {
                    continue;
                }
                if (fieldMetadata.field_name !== fieldName) {
                    ctx.addIssue({
                        code: 'custom',
                        message:
                            'generation metadata field_name must match map key',
                        path: ['fields', fieldName, 'field_name'],
                    });
                }
                if (!metadata.field_names.includes(fieldName)) {
                    ctx.addIssue({
                        code: 'custom',
                        message:
                            'generation metadata fields must be listed in field_names',
                        path: ['fields', fieldName],
                    });
                }
            }
        });

export const CredentialRotationAttemptSummarySchema: z.ZodType<CredentialRotationAttemptSummary> =
    z
        .object({
            credential_rotation_attempt_uuid: z.string().uuid(),
            credential_uuid: z.string().uuid(),
            ipx_credential_definition_uuid: z.string().uuid(),
            organization_uuid: z.string().uuid(),
            role_assignment_uuid: z.string().uuid().nullable(),
            actor_role_assignment_uuid: z.string().uuid().nullable(),
            state: CredentialRotationAttemptStateSchema,
            flow_key: NonEmptyStringSchema,
            target_field_names: z.array(IpxCredentialFieldNameSchema).min(1),
            created_ts: z.string().datetime(),
            expires_ts: z.string().datetime(),
            injected_ts: z.string().datetime().nullable(),
            submitted_ts: z.string().datetime().nullable(),
            promoted_ts: z.string().datetime().nullable(),
            failed_ts: z.string().datetime().nullable(),
            evidence_kind: CredentialRotationEvidenceKindSchema.nullable(),
        })
        .strict();

export const CredentialRotationBeginRequestSchema: z.ZodType<CredentialRotationBeginRequest> =
    z
        .object({
            ipx_credential_definition_uuid: z.string().uuid(),
            credential_uuid: z.string().uuid(),
            model_digest: IpxWebResourceModelDigestSchema,
            flow_key: NonEmptyStringSchema,
            screen_key: NonEmptyStringSchema,
        })
        .strict();

export const CredentialRotationBeginResultSchema: z.ZodType<CredentialRotationBeginResultType> =
    z.enum(CredentialRotationBeginResult);

export const CredentialRotationBeginFieldBindingValueSchema: z.ZodType<CredentialRotationBeginFieldBindingValue> =
    z
        .object({
            credential_field_name: IpxCredentialFieldNameSchema,
            source: IpxFlowFieldBindingSourceSchema,
            screen_key: NonEmptyStringSchema,
            element_key: NonEmptyStringSchema,
            field_value: z.string(),
        })
        .strict();

export const CredentialRotationBeginResolvedResponseSchema: z.ZodType<CredentialRotationBeginResolvedResponse> =
    z
        .object({
            result: z.literal(CredentialRotationBeginResult.RESOLVED),
            credential_rotation_attempt_uuid: z.string().uuid(),
            state: CredentialRotationAttemptStateSchema,
            expires_ts: z.string().datetime(),
            field_names: z.array(IpxCredentialFieldNameSchema).min(1),
            field_binding_values: z
                .array(CredentialRotationBeginFieldBindingValueSchema)
                .min(1),
        })
        .strict()
        .superRefine((response, ctx) => {
            for (const [index, binding] of response.field_binding_values.entries()) {
                if (!response.field_names.includes(binding.credential_field_name)) {
                    ctx.addIssue({
                        code: 'custom',
                        message:
                            'rotation begin binding values must be listed in field_names',
                        path: [
                            'field_binding_values',
                            index,
                            'credential_field_name',
                        ],
                    });
                }
            }
        });

export const CredentialRotationBeginActiveRecoveryRequiredResponseSchema: z.ZodType<CredentialRotationBeginActiveRecoveryRequiredResponse> =
    z
        .object({
            result: z.literal(
                CredentialRotationBeginResult.ACTIVE_RECOVERY_REQUIRED
            ),
            active_attempt: CredentialRotationAttemptSummarySchema,
        })
        .strict();

export const CredentialRotationBeginMissingCredentialResponseSchema: z.ZodType<CredentialRotationBeginMissingCredentialResponse> =
    z
        .object({
            result: z.literal(CredentialRotationBeginResult.MISSING_CREDENTIAL),
        })
        .strict();

export const CredentialRotationBeginNotRotatableResponseSchema: z.ZodType<CredentialRotationBeginNotRotatableResponse> =
    z
        .object({
            result: z.literal(CredentialRotationBeginResult.NOT_ROTATABLE),
            reason: NonEmptyStringSchema,
        })
        .strict();

export const CredentialRotationBeginStaleProjectionResponseSchema: z.ZodType<CredentialRotationBeginStaleProjectionResponse> =
    z
        .object({
            result: z.literal(CredentialRotationBeginResult.STALE_PROJECTION),
            current_model_digest: IpxWebResourceModelDigestSchema,
        })
        .strict();

export const CredentialRotationBeginUnauthorizedResponseSchema: z.ZodType<CredentialRotationBeginUnauthorizedResponse> =
    z
        .object({
            result: z.literal(CredentialRotationBeginResult.UNAUTHORIZED),
        })
        .strict();

export const CredentialRotationBeginResponseSchema: z.ZodType<CredentialRotationBeginResponse> =
    z.union([
        CredentialRotationBeginResolvedResponseSchema,
        CredentialRotationBeginActiveRecoveryRequiredResponseSchema,
        CredentialRotationBeginMissingCredentialResponseSchema,
        CredentialRotationBeginNotRotatableResponseSchema,
        CredentialRotationBeginStaleProjectionResponseSchema,
        CredentialRotationBeginUnauthorizedResponseSchema,
    ]);

export const CredentialRotationInjectedRequestSchema: z.ZodType<CredentialRotationInjectedRequest> =
    z
        .object({
            credential_rotation_attempt_uuid: z.string().uuid(),
            model_digest: IpxWebResourceModelDigestSchema,
            screen_key: NonEmptyStringSchema,
            field_names: z.array(IpxCredentialFieldNameSchema).min(1),
            injection_evidence: CredentialRotationInjectionEvidenceSchema,
        })
        .strict()
        .superRefine((request, ctx) => {
            for (const fieldName of request.injection_evidence.field_names) {
                if (!request.field_names.includes(fieldName)) {
                    ctx.addIssue({
                        code: 'custom',
                        message:
                            'injection evidence field names must be listed on the request',
                        path: ['injection_evidence', 'field_names'],
                    });
                }
            }
        });

export const CredentialRotationSubmittedRequestSchema: z.ZodType<CredentialRotationSubmittedRequest> =
    z
        .object({
            credential_rotation_attempt_uuid: z.string().uuid(),
            model_digest: IpxWebResourceModelDigestSchema,
            flow_key: NonEmptyStringSchema,
            screen_key: NonEmptyStringSchema,
            submission_evidence:
                CredentialRotationSubmissionTriggerEvidenceSchema,
        })
        .strict()
        .superRefine((request, ctx) => {
            if (request.submission_evidence.flow_key !== request.flow_key) {
                ctx.addIssue({
                    code: 'custom',
                    message:
                        'submission evidence flow_key must match the request flow_key',
                    path: ['submission_evidence', 'flow_key'],
                });
            }
            if (request.submission_evidence.screen_key !== request.screen_key) {
                ctx.addIssue({
                    code: 'custom',
                    message:
                        'submission evidence screen_key must match the request screen_key',
                    path: ['submission_evidence', 'screen_key'],
                });
            }
        });

export const CredentialRotationPromoteRequestSchema: z.ZodType<CredentialRotationPromoteRequest> =
    z
        .object({
            credential_rotation_attempt_uuid: z.string().uuid(),
            model_digest: IpxWebResourceModelDigestSchema,
            flow_key: NonEmptyStringSchema,
            success_evidence: CredentialRotationDomSuccessEvidenceSchema,
        })
        .strict()
        .superRefine((request, ctx) => {
            if (request.success_evidence.flow_key !== request.flow_key) {
                ctx.addIssue({
                    code: 'custom',
                    message:
                        'success evidence flow_key must match the request flow_key',
                    path: ['success_evidence', 'flow_key'],
                });
            }
        });

export const CredentialRotationPromoteResultSchema: z.ZodType<CredentialRotationPromoteResultType> =
    z.enum(CredentialRotationPromoteResult);

export const CredentialRotationPromoteSucceededResponseSchema: z.ZodType<CredentialRotationPromoteSucceededResponse> =
    z
        .object({
            result: z.literal(CredentialRotationPromoteResult.PROMOTED),
            attempt: CredentialRotationAttemptSummarySchema,
        })
        .strict();

export const CredentialRotationPromoteInvalidStateResponseSchema: z.ZodType<CredentialRotationPromoteInvalidStateResponse> =
    z
        .object({
            result: z.literal(CredentialRotationPromoteResult.INVALID_STATE),
            current_state: CredentialRotationAttemptStateSchema,
        })
        .strict();

export const CredentialRotationPromoteExpiredAttemptResponseSchema: z.ZodType<CredentialRotationPromoteExpiredAttemptResponse> =
    z
        .object({
            result: z.literal(CredentialRotationPromoteResult.EXPIRED_ATTEMPT),
            expires_ts: z.string().datetime({ offset: true }),
        })
        .strict();

export const CredentialRotationPromoteStaleProjectionResponseSchema: z.ZodType<CredentialRotationPromoteStaleProjectionResponse> =
    z
        .object({
            result: z.literal(CredentialRotationPromoteResult.STALE_PROJECTION),
            current_model_digest: IpxWebResourceModelDigestSchema,
        })
        .strict();

export const CredentialRotationPromoteUnauthorizedResponseSchema: z.ZodType<CredentialRotationPromoteUnauthorizedResponse> =
    z
        .object({
            result: z.literal(CredentialRotationPromoteResult.UNAUTHORIZED),
        })
        .strict();

export const CredentialRotationPromotePromotionFailedResponseSchema: z.ZodType<CredentialRotationPromotePromotionFailedResponse> =
    z
        .object({
            result: z.literal(CredentialRotationPromoteResult.PROMOTION_FAILED),
            attempt: CredentialRotationAttemptSummarySchema,
            reason: NonEmptyStringSchema,
        })
        .strict();

export const CredentialRotationPromoteResponseSchema: z.ZodType<CredentialRotationPromoteResponse> =
    z.union([
        CredentialRotationPromoteSucceededResponseSchema,
        CredentialRotationPromoteInvalidStateResponseSchema,
        CredentialRotationPromoteExpiredAttemptResponseSchema,
        CredentialRotationPromoteStaleProjectionResponseSchema,
        CredentialRotationPromoteUnauthorizedResponseSchema,
        CredentialRotationPromotePromotionFailedResponseSchema,
    ]);

export const CredentialRotationFailRequestSchema: z.ZodType<CredentialRotationFailRequest> =
    z
        .object({
            credential_rotation_attempt_uuid: z.string().uuid(),
            model_digest: IpxWebResourceModelDigestSchema,
            flow_key: NonEmptyStringSchema,
            failure_evidence: CredentialRotationDomFailureEvidenceSchema,
        })
        .strict()
        .superRefine((request, ctx) => {
            if (request.failure_evidence.flow_key !== request.flow_key) {
                ctx.addIssue({
                    code: 'custom',
                    message:
                        'failure evidence flow_key must match the request flow_key',
                    path: ['failure_evidence', 'flow_key'],
                });
            }
        });

export const ManualCredentialCaptureRequestSchema: z.ZodType<ManualCredentialCaptureRequest> =
    z
        .object({
            ipx_credential_definition_uuid: z.string().uuid(),
            credential_uuid: z.string().uuid().optional(),
            model_digest: IpxWebResourceModelDigestSchema,
            login_flow_key: NonEmptyStringSchema,
            captured_field_patch: z.record(
                IpxCredentialFieldNameSchema,
                NonEmptyStringSchema
            ),
            success_evidence: CredentialRotationDomSuccessEvidenceSchema,
        })
        .strict()
        .superRefine((request, ctx) => {
            if (Object.keys(request.captured_field_patch).length === 0) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'captured_field_patch must contain at least one field',
                    path: ['captured_field_patch'],
                });
            }
            if (request.success_evidence.flow_key !== request.login_flow_key) {
                ctx.addIssue({
                    code: 'custom',
                    message:
                        'success evidence flow_key must match the login_flow_key',
                    path: ['success_evidence', 'flow_key'],
                });
            }
        });

const ManualCredentialCaptureSucceededResponseBaseSchema = z
    .object({
        credential_uuid: z.string().uuid(),
        ipx_credential_definition_uuid: z.string().uuid(),
        account_id: NonEmptyStringSchema,
        masked_preview: z.string(),
        updated_field_names: z.array(IpxCredentialFieldNameSchema).min(1),
    })
    .strict();

export const ManualCredentialCaptureResultSchema: z.ZodType<ManualCredentialCaptureResultType> =
    z.enum(ManualCredentialCaptureResult);

export const ManualCredentialCaptureCreatedResponseSchema: z.ZodType<ManualCredentialCaptureCreatedResponse> =
    ManualCredentialCaptureSucceededResponseBaseSchema.extend({
        result: z.literal(ManualCredentialCaptureResult.CREATED),
    });

export const ManualCredentialCaptureUpdatedResponseSchema: z.ZodType<ManualCredentialCaptureUpdatedResponse> =
    ManualCredentialCaptureSucceededResponseBaseSchema.extend({
        result: z.literal(ManualCredentialCaptureResult.UPDATED),
    });

export const ManualCredentialCaptureUnauthorizedResponseSchema: z.ZodType<ManualCredentialCaptureUnauthorizedResponse> =
    z
        .object({
            result: z.literal(ManualCredentialCaptureResult.UNAUTHORIZED),
        })
        .strict();

export const ManualCredentialCaptureStaleProjectionResponseSchema: z.ZodType<ManualCredentialCaptureStaleProjectionResponse> =
    z
        .object({
            result: z.literal(ManualCredentialCaptureResult.STALE_PROJECTION),
            current_model_digest: IpxWebResourceModelDigestSchema,
        })
        .strict();

export const ManualCredentialCaptureInvalidEvidenceResponseSchema: z.ZodType<ManualCredentialCaptureInvalidEvidenceResponse> =
    z
        .object({
            result: z.literal(ManualCredentialCaptureResult.INVALID_EVIDENCE),
            reason: NonEmptyStringSchema,
        })
        .strict();

export const ManualCredentialCapturePersistenceFailedResponseSchema: z.ZodType<ManualCredentialCapturePersistenceFailedResponse> =
    z
        .object({
            result: z.literal(ManualCredentialCaptureResult.PERSISTENCE_FAILED),
            reason: NonEmptyStringSchema,
        })
        .strict();

export const ManualCredentialCaptureResponseSchema: z.ZodType<ManualCredentialCaptureResponse> =
    z.union([
        ManualCredentialCaptureCreatedResponseSchema,
        ManualCredentialCaptureUpdatedResponseSchema,
        ManualCredentialCaptureUnauthorizedResponseSchema,
        ManualCredentialCaptureStaleProjectionResponseSchema,
        ManualCredentialCaptureInvalidEvidenceResponseSchema,
        ManualCredentialCapturePersistenceFailedResponseSchema,
    ]);

export const CredentialRotationHistoryRequestSchema: z.ZodType<CredentialRotationHistoryRequest> =
    z
        .object({
            credential_uuid: z.string().uuid(),
            limit: AuthorizationPaginationLimitQuerySchema,
            cursor: AuthorizationOpaqueCursorQuerySchema,
            state: CredentialRotationAttemptStateSchema.optional(),
        })
        .strict();

export const CredentialRotationHistoryCapabilitiesSchema: z.ZodType<CredentialRotationHistoryCapabilities> =
    z
        .object({
            can_view_history: z.boolean(),
        })
        .strict();

export const CredentialRotationHistoryAttemptCapabilitiesSchema: z.ZodType<CredentialRotationHistoryAttemptCapabilities> =
    z
        .object({
            can_manual_promote: z.boolean(),
            manual_promote_disabled_reason: NonEmptyStringSchema.optional(),
        })
        .strict();

export const CredentialRotationHistoryTargetFieldDisplaySchema: z.ZodType<CredentialRotationHistoryTargetFieldDisplay> =
    z
        .object({
            field_name: IpxCredentialFieldNameSchema,
            label: NonEmptyStringSchema,
        })
        .strict();

export const CredentialRotationHistoryDisplayMetadataSchema: z.ZodType<CredentialRotationHistoryDisplayMetadata> =
    z
        .object({
            ipx_credential_definition_name: NonEmptyStringSchema,
            account_label: NonEmptyStringSchema,
            target_fields: z.array(
                CredentialRotationHistoryTargetFieldDisplaySchema
            ),
            flow_display_name: NonEmptyStringSchema.nullable(),
            actor_role_assignment_label: NonEmptyStringSchema.nullable(),
        })
        .strict();

export const CredentialRotationHistoryListItemSchema: z.ZodType<CredentialRotationHistoryListItem> =
    z
        .object({
            attempt: CredentialRotationAttemptSummarySchema,
            display: CredentialRotationHistoryDisplayMetadataSchema,
            capabilities: CredentialRotationHistoryAttemptCapabilitiesSchema,
        })
        .strict();

export const CredentialRotationHistoryResponseSchema: z.ZodType<CredentialRotationHistoryResponse> =
    z
        .object({
            attempts: z.array(CredentialRotationHistoryListItemSchema),
            pagination: OpaqueCursorPaginationSchema,
            capabilities: CredentialRotationHistoryCapabilitiesSchema,
        })
        .strict();

export const CredentialRotationManualPromotionConfirmationSchema: z.ZodType<CredentialRotationManualPromotionConfirmation> =
    z
        .object({
            confirmed: z.literal(true),
            note: NonEmptyStringSchema.optional(),
        })
        .strict();

export const CredentialRotationManualPromotionRequestSchema: z.ZodType<CredentialRotationManualPromotionRequest> =
    z
        .object({
            credential_rotation_attempt_uuid: z.string().uuid(),
            confirmation: CredentialRotationManualPromotionConfirmationSchema,
        })
        .strict();

export const CredentialRotationManualPromotionResultSchema: z.ZodType<CredentialRotationManualPromotionResultType> =
    z.enum(CredentialRotationManualPromotionResult);

export const CredentialRotationManualPromotionSucceededResponseSchema: z.ZodType<CredentialRotationManualPromotionSucceededResponse> =
    z
        .object({
            result: z.literal(CredentialRotationManualPromotionResult.PROMOTED),
            attempt: CredentialRotationAttemptSummarySchema,
        })
        .strict();

export const CredentialRotationManualPromotionUnauthorizedResponseSchema: z.ZodType<CredentialRotationManualPromotionUnauthorizedResponse> =
    z
        .object({
            result: z.literal(
                CredentialRotationManualPromotionResult.UNAUTHORIZED
            ),
        })
        .strict();

export const CredentialRotationManualPromotionInvalidStateResponseSchema: z.ZodType<CredentialRotationManualPromotionInvalidStateResponse> =
    z
        .object({
            result: z.literal(
                CredentialRotationManualPromotionResult.INVALID_STATE
            ),
            current_state: CredentialRotationAttemptStateSchema,
        })
        .strict();

export const CredentialRotationManualPromotionExpiredAttemptResponseSchema: z.ZodType<CredentialRotationManualPromotionExpiredAttemptResponse> =
    z
        .object({
            result: z.literal(
                CredentialRotationManualPromotionResult.EXPIRED_ATTEMPT
            ),
            expires_ts: z.string().datetime({ offset: true }),
        })
        .strict();

export const CredentialRotationManualPromotionStaleTargetResponseSchema: z.ZodType<CredentialRotationManualPromotionStaleTargetResponse> =
    z
        .object({
            result: z.literal(
                CredentialRotationManualPromotionResult.STALE_TARGET
            ),
            reason: NonEmptyStringSchema,
        })
        .strict();

export const CredentialRotationManualPromotionPromotionFailedResponseSchema: z.ZodType<CredentialRotationManualPromotionPromotionFailedResponse> =
    z
        .object({
            result: z.literal(
                CredentialRotationManualPromotionResult.PROMOTION_FAILED
            ),
            attempt: CredentialRotationAttemptSummarySchema,
            reason: NonEmptyStringSchema,
        })
        .strict();

export const CredentialRotationManualPromotionResponseSchema: z.ZodType<CredentialRotationManualPromotionResponse> =
    z.union([
        CredentialRotationManualPromotionSucceededResponseSchema,
        CredentialRotationManualPromotionUnauthorizedResponseSchema,
        CredentialRotationManualPromotionInvalidStateResponseSchema,
        CredentialRotationManualPromotionExpiredAttemptResponseSchema,
        CredentialRotationManualPromotionStaleTargetResponseSchema,
        CredentialRotationManualPromotionPromotionFailedResponseSchema,
    ]);

function hasOverlappingCharacters(first: string, second: string): boolean {
    const firstCharacters = new Set(first.split(''));
    return second.split('').some((character) => firstCharacters.has(character));
}

export const IpxCredentialFieldValidationPolicySchema: z.ZodType<IpxCredentialFieldValidationPolicy> =
    z
        .object({
            min_length: z.number().int().nonnegative().optional(),
            max_length: z.number().int().nonnegative().optional(),
            pattern: z
                .string()
                .min(1)
                .refine((pattern) => {
                    try {
                        new RegExp(pattern);
                        return true;
                    } catch {
                        return false;
                    }
                }, 'pattern must be a valid regular expression')
                .optional(),
            allowed_characters: z.string().min(1).optional(),
            forbidden_characters: z.string().min(1).optional(),
        })
        .strict()
        .superRefine((value, context) => {
            if (
                value.min_length !== undefined &&
                value.max_length !== undefined &&
                value.min_length > value.max_length
            ) {
                context.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'min_length must be less than or equal to max_length',
                    path: ['min_length'],
                });
            }

            if (
                value.allowed_characters !== undefined &&
                value.forbidden_characters !== undefined &&
                hasOverlappingCharacters(
                    value.allowed_characters,
                    value.forbidden_characters
                )
            ) {
                context.addIssue({
                    code: z.ZodIssueCode.custom,
                    message:
                        'allowed_characters and forbidden_characters must not overlap',
                    path: ['forbidden_characters'],
                });
            }
        });

export const IpxCredentialFieldGenerationPolicySchema: z.ZodType<IpxCredentialFieldGenerationPolicy> =
    z
        .object({
            min_length: z.number().int().nonnegative().optional(),
            max_length: z.number().int().nonnegative().optional(),
            allowed_characters: z.string().min(1).optional(),
            required_classes: z
                .array(IpxCredentialFieldCharacterClassSchema)
                .min(1)
                .optional(),
            forbidden_characters: z.string().min(1).optional(),
        })
        .strict()
        .superRefine((value, context) => {
            if (
                value.min_length !== undefined &&
                value.max_length !== undefined &&
                value.min_length > value.max_length
            ) {
                context.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'min_length must be less than or equal to max_length',
                    path: ['min_length'],
                });
            }

            if (
                value.allowed_characters !== undefined &&
                value.forbidden_characters !== undefined &&
                hasOverlappingCharacters(
                    value.allowed_characters,
                    value.forbidden_characters
                )
            ) {
                context.addIssue({
                    code: z.ZodIssueCode.custom,
                    message:
                        'allowed_characters and forbidden_characters must not overlap',
                    path: ['forbidden_characters'],
                });
            }
        });

export const IpxCredentialFieldDisplayConfigSchema: z.ZodType<IpxCredentialFieldDisplayConfig> =
    z
        .object({
            label: NonEmptyStringSchema,
        })
        .strict();

export const IpxCredentialFieldConfigSchema: z.ZodType<IpxCredentialFieldConfig> =
    z
        .object({
            field_name: IpxCredentialFieldNameSchema,
            field_kind: z.enum(IpxCredentialFieldKind),
            field_classification: z.enum(IpxCredentialFieldClassification),
            required: z.boolean(),
            display: IpxCredentialFieldDisplayConfigSchema,
            validation: IpxCredentialFieldValidationPolicySchema,
            generation: IpxCredentialFieldGenerationPolicySchema,
            rotatable: z.boolean(),
            confirmation_field_name: IpxCredentialFieldNameSchema.optional(),
        })
        .strict();

function refineIpxCredentialFieldConfigMap(
    value: IpxCredentialFieldConfigMap,
    context: z.RefinementCtx
): void {
    const seenFieldNames = new Set<string>();
    for (const fieldKey of Object.keys(value)) {
        const fieldConfig = value[fieldKey];
        if (fieldConfig === undefined) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'credential field map value is missing',
                path: [fieldKey],
            });
            continue;
        }
        if (fieldConfig.field_name !== fieldKey) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'field_name must match the credential field map key',
                path: [fieldKey, 'field_name'],
            });
        }

        if (seenFieldNames.has(fieldConfig.field_name)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'credential field map contains a duplicate field_name',
                path: [fieldKey, 'field_name'],
            });
        }
        seenFieldNames.add(fieldConfig.field_name);
    }
}

export const IpxCredentialFieldConfigMapSchema: z.ZodType<IpxCredentialFieldConfigMap> =
    z
        .record(IpxCredentialFieldNameSchema, IpxCredentialFieldConfigSchema)
        .superRefine(refineIpxCredentialFieldConfigMap);

export const NonEmptyIpxCredentialFieldConfigMapSchema: z.ZodType<IpxCredentialFieldConfigMap> =
    IpxCredentialFieldConfigMapSchema.superRefine((value, context) => {
        if (Object.keys(value).length === 0) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                    'credential field config map must contain at least one field',
            });
        }
    });

export const IpxCredentialFieldDescriptorSchema: z.ZodType<IpxCredentialFieldDescriptor> =
    z
        .object({
            field_name: NonEmptyStringSchema,
            field_kind: z.enum(IpxCredentialFieldKind),
            field_classification: z.enum(IpxCredentialFieldClassification),
            required: z.boolean(),
            display: IpxCredentialFieldDisplayConfigSchema,
            rotatable: z.boolean().optional(),
            confirmation_field_name: IpxCredentialFieldNameSchema.optional(),
        })
        .strict();

export const SecretCredentialDefinitionListItemSchema: z.ZodType<SecretCredentialDefinitionListItem> =
    z
        .object({
            ipx_credential_definition_uuid: z.string().uuid(),
            system_id: SupportedCredentialSystemIdSchema,
            credential_type: z.enum(CredentialType),
            name: NonEmptyStringSchema,
            credential_definition_key: z.string(),
            credential_owner_scope: z.enum(CredentialOwnerScope),
            status: z.enum(Status),
            field_descriptors: z.array(IpxCredentialFieldDescriptorSchema),
        })
        .strict();

export const ListSecretCredentialDefinitionsQuerySchema: z.ZodType<ListSecretCredentialDefinitionsQuery> =
    z
        .object({
            organization_uuid: z.string().uuid(),
            system_ids: z.array(SupportedCredentialSystemIdSchema).optional(),
            credential_types: z.array(z.enum(CredentialType)).optional(),
        })
        .strict();

export const ListSecretCredentialDefinitionsResponseSchema: z.ZodType<ListSecretCredentialDefinitionsResponse> =
    z
        .object({
            definitions: z.array(SecretCredentialDefinitionListItemSchema),
        })
        .strict();

export const PortalCredentialRotationFlowSummarySchema: z.ZodType<PortalCredentialRotationFlowSummary> =
    z
        .object({
            flow_key: NonEmptyStringSchema,
            display_name: NonEmptyStringSchema,
            target_field_names: z.array(IpxCredentialFieldNameSchema).min(1),
        })
        .strict();

export const PortalCredentialRotationRecoverySummarySchema: z.ZodType<PortalCredentialRotationRecoverySummary> =
    z
        .object({
            severity: CredentialRotationRecoverySeveritySchema,
            message: NonEmptyStringSchema,
        })
        .strict();

export const PortalCredentialRotationEligibilitySchema: z.ZodType<PortalCredentialRotationEligibility> =
    z
        .object({
            ipx_credential_definition_uuid: z.string().uuid(),
            credential_uuid: z.string().uuid().nullable(),
            flow_summaries: z.array(PortalCredentialRotationFlowSummarySchema),
            rotatable_field_descriptors: z.array(
                IpxCredentialFieldDescriptorSchema
            ),
            active_attempt: CredentialRotationAttemptSummarySchema.nullable(),
            can_rotate: z.boolean(),
            rotate_disabled_reason:
                CredentialRotationDisabledReasonSchema.nullable(),
            can_view_history: z.boolean(),
            history_disabled_reason:
                CredentialRotationDisabledReasonSchema.nullable(),
            recovery_summary:
                PortalCredentialRotationRecoverySummarySchema.nullable(),
        })
        .strict()
        .superRefine((eligibility, ctx) => {
            if (
                eligibility.can_rotate &&
                eligibility.rotate_disabled_reason !== null
            ) {
                ctx.addIssue({
                    code: 'custom',
                    message:
                        'rotate_disabled_reason must be null when can_rotate is true',
                    path: ['rotate_disabled_reason'],
                });
            }
            if (
                !eligibility.can_rotate &&
                eligibility.rotate_disabled_reason === null
            ) {
                ctx.addIssue({
                    code: 'custom',
                    message:
                        'rotate_disabled_reason is required when can_rotate is false',
                    path: ['rotate_disabled_reason'],
                });
            }
            if (
                eligibility.can_view_history &&
                eligibility.history_disabled_reason !== null
            ) {
                ctx.addIssue({
                    code: 'custom',
                    message:
                        'history_disabled_reason must be null when can_view_history is true',
                    path: ['history_disabled_reason'],
                });
            }
            if (
                !eligibility.can_view_history &&
                eligibility.history_disabled_reason === null
            ) {
                ctx.addIssue({
                    code: 'custom',
                    message:
                        'history_disabled_reason is required when can_view_history is false',
                    path: ['history_disabled_reason'],
                });
            }
        });

export const IpxCredentialSummaryCapabilitiesSchema: z.ZodType<IpxCredentialSummaryCapabilities> =
    z
        .object({
            can_display: z.boolean(),
            can_edit: z.boolean(),
            can_delete: z.boolean(),
            can_disable: z.boolean(),
            can_enable: z.boolean(),
            can_restore: z.boolean(),
            can_reveal_secret_fields: z.boolean(),
            reveal_secret_fields_disabled_reason: NonEmptyStringSchema.optional(),
            can_rotate: z.boolean(),
            rotate_disabled_reason:
                CredentialRotationDisabledReasonSchema.nullable(),
            can_view_history: z.boolean(),
            history_disabled_reason:
                CredentialRotationDisabledReasonSchema.nullable(),
        })
        .strict()
        .superRefine((capabilities, ctx) => {
            if (
                capabilities.can_rotate &&
                capabilities.rotate_disabled_reason !== null
            ) {
                ctx.addIssue({
                    code: 'custom',
                    message:
                        'rotate_disabled_reason must be null when can_rotate is true',
                    path: ['rotate_disabled_reason'],
                });
            }
            if (
                !capabilities.can_rotate &&
                capabilities.rotate_disabled_reason === null
            ) {
                ctx.addIssue({
                    code: 'custom',
                    message:
                        'rotate_disabled_reason is required when can_rotate is false',
                    path: ['rotate_disabled_reason'],
                });
            }
            if (
                capabilities.can_view_history &&
                capabilities.history_disabled_reason !== null
            ) {
                ctx.addIssue({
                    code: 'custom',
                    message:
                        'history_disabled_reason must be null when can_view_history is true',
                    path: ['history_disabled_reason'],
                });
            }
            if (
                !capabilities.can_view_history &&
                capabilities.history_disabled_reason === null
            ) {
                ctx.addIssue({
                    code: 'custom',
                    message:
                        'history_disabled_reason is required when can_view_history is false',
                    path: ['history_disabled_reason'],
                });
            }
        });

export const IpxCredentialOwnerMetadataSchema: z.ZodType<IpxCredentialOwnerMetadata> =
    z
        .object({
            role_assignment_uuid: z.string().uuid(),
            user_uuid: z.string().uuid(),
            label: NonEmptyStringSchema,
            role: z.enum(Role),
            role_assignment_status: z.enum(Status),
        })
        .strict();

export const IpxCredentialSummaryListItemSchema: z.ZodType<IpxCredentialSummaryListItem> =
    z
        .object({
            credential_uuid: z.string().uuid(),
            organization_ipx_credential_policy_uuid: z.string().uuid(),
            ipx_credential_definition_uuid: z.string().uuid(),
            scope: IpxCredentialScopeSchema,
            owner: IpxCredentialOwnerMetadataSchema.nullable().optional(),
            account_id: NonEmptyStringSchema,
            status: z.enum(Status),
            public_fields: z.record(NonEmptyStringSchema, z.string()),
            masked_preview: z.string(),
            field_descriptors: z.array(IpxCredentialFieldDescriptorSchema),
            capabilities: IpxCredentialSummaryCapabilitiesSchema,
            rotation_eligibility:
                PortalCredentialRotationEligibilitySchema.nullable(),
        })
        .strict();

export const IpxCredentialCreateCapabilitySchema: z.ZodType<IpxCredentialCreateCapability> =
    z
        .object({
            can_create: z.boolean(),
            disabled_reason: NonEmptyStringSchema.optional(),
        })
        .strict();

export const EligibleIpxCredentialRoleAssignmentListItemSchema: z.ZodType<EligibleIpxCredentialRoleAssignmentListItem> =
    z
        .object({
            role_assignment_uuid: z.string().uuid(),
            user_uuid: z.string().uuid(),
            label: NonEmptyStringSchema,
            role: z.enum(Role),
            role_assignment_status: z.enum(Status),
            create_capability: IpxCredentialCreateCapabilitySchema,
        })
        .strict();

export const IpxCredentialPolicyCredentialCollectionCapabilitiesSchema: z.ZodType<IpxCredentialPolicyCredentialCollectionCapabilities> =
    z
        .object({
            can_create_organization_credential: z.boolean(),
            can_create_role_assignment_credential: z.boolean(),
        })
        .strict();

export const ListIpxCredentialsForPolicyResponseSchema: z.ZodType<ListIpxCredentialsForPolicyResponse> =
    z
        .object({
            policy: OrganizationIpxCredentialPolicyListItemSchema,
            capabilities:
                IpxCredentialPolicyCredentialCollectionCapabilitiesSchema,
            field_descriptors: z.array(IpxCredentialFieldDescriptorSchema),
            credentials: z.array(IpxCredentialSummaryListItemSchema),
            eligible_missing_role_assignments: z.array(
                EligibleIpxCredentialRoleAssignmentListItemSchema
            ),
        })
        .strict();

export const DashboardWebsiteCredentialPolicyListItemSchema: z.ZodType<DashboardWebsiteCredentialPolicyListItem> =
    z
        .object({
            organization_ipx_credential_policy_uuid: z.string().uuid(),
            organization_uuid: z.string().uuid(),
            ipx_credential_definition_uuid: z.string().uuid(),
            ipx_credential_definition_name: NonEmptyStringSchema,
            default_account_id: NonEmptyStringSchema,
            system_id: SupportedCredentialSystemIdSchema,
            credential_definition_key: z.string(),
            status: z.enum(Status),
        })
        .strict();

export const DashboardWebsiteCredentialSlotSchema: z.ZodType<DashboardWebsiteCredentialSlot> =
    z
        .object({
            policy: DashboardWebsiteCredentialPolicyListItemSchema,
            field_descriptors: z.array(IpxCredentialFieldDescriptorSchema),
            rotation_eligibility:
                PortalCredentialRotationEligibilitySchema.nullable(),
            default_account_id: NonEmptyStringSchema,
            credentials: z.array(IpxCredentialSummaryListItemSchema),
            can_create: z.boolean(),
        })
        .strict();

export const ListDashboardWebsiteCredentialsResponseSchema: z.ZodType<ListDashboardWebsiteCredentialsResponse> =
    z
        .object({
            credential_slots: z.array(DashboardWebsiteCredentialSlotSchema),
        })
        .strict();

export const IpxCredentialEditedFieldsSchema: z.ZodType<IpxCredentialEditedFields> =
    z.record(NonEmptyStringSchema, z.string());

export const IpxCredentialSaveFieldsSchema: z.ZodType<IpxCredentialSaveFields> = z
    .object({
        account_id: NonEmptyStringSchema,
        edited_fields: IpxCredentialEditedFieldsSchema,
    })
    .strict();

export const CreateIpxCredentialRequestSchema: z.ZodType<CreateIpxCredentialRequest> =
    IpxCredentialSaveFieldsSchema.and(
        z
            .object({
                scope: IpxCredentialScopeSchema,
            })
            .strict()
    );

export const CreateDashboardWebsiteCredentialRequestSchema: z.ZodType<CreateDashboardWebsiteCredentialRequest> =
    IpxCredentialSaveFieldsSchema.and(
        z
            .object({
                organization_uuid: z.string().uuid(),
                organization_ipx_credential_policy_uuid: z.string().uuid(),
            })
            .strict()
    );

export const UpdateIpxCredentialRequestSchema: z.ZodType<UpdateIpxCredentialRequest> =
    IpxCredentialSaveFieldsSchema.and(
        z
            .object({
                credential_uuid: z.string().uuid(),
            })
            .strict()
    );

export const RevealIpxCredentialSecretFieldRequestSchema: z.ZodType<RevealIpxCredentialSecretFieldRequest> =
    z
        .object({
            credential_uuid: z.string().uuid(),
            field_name: NonEmptyStringSchema,
        })
        .strict();

export const RevealIpxCredentialSecretFieldResponseSchema: z.ZodType<RevealIpxCredentialSecretFieldResponse> =
    z
        .object({
            field_name: NonEmptyStringSchema,
            field_value: z.string(),
        })
        .strict();

export const IpxCredentialRuntimeRoleScopeSchema: z.ZodType<IpxCredentialRuntimeRoleScope> =
    z.object({
        organization_uuid: z.string().uuid(),
        role_assignment_uuid: z.string().uuid(),
    });

export const IpxCredentialMaskedCredentialMetadataSchema: z.ZodType<IpxCredentialMaskedCredentialMetadata> =
    z.object({
        credential_uuid: z.string().uuid(),
        credential_owner_scope: CredentialOwnerScopeSchema,
        account_id: NonEmptyStringSchema,
        masked_preview: z.string(),
        status: z.enum(Status),
    });

export const IpxCredentialRuntimeWorkflowProjectionSchema: z.ZodType<IpxCredentialRuntimeWorkflowProjection> =
    z.object({
        role_scope: IpxCredentialRuntimeRoleScopeSchema,
        definition: IpxWebLoginCredentialDefinitionProjectionSchema,
        credential: IpxCredentialMaskedCredentialMetadataSchema.nullable(),
        revision_hash: NonEmptyStringSchema,
    });

export const IpxCredentialRuntimeBootstrapRequestSchema: z.ZodType<IpxCredentialRuntimeBootstrapRequest> =
    z.object({
        role_scope: IpxCredentialRuntimeRoleScopeSchema,
    });

export const IpxCredentialRuntimeBootstrapResponseSchema: z.ZodType<IpxCredentialRuntimeBootstrapResponse> =
    z.object({
        role_scope: IpxCredentialRuntimeRoleScopeSchema,
        workflow_projections: z.array(IpxCredentialRuntimeWorkflowProjectionSchema),
    });

export const IpxCredentialDefinitionIdentitySchema: z.ZodType<IpxCredentialDefinitionIdentity> =
    z.object({
        ipx_credential_definition_uuid: z.string().uuid(),
        system_id: SupportedCredentialSystemIdSchema,
        credential_definition_key: z.string(),
    });

export const IpxCredentialRuntimeMatchedUrlDataSchema: z.ZodType<IpxCredentialRuntimeMatchedUrlData> =
    z.object({
        page_url: z.string().url(),
        matched_url: z.string().url(),
        matched_url_pattern: UrlPatternStringSchema,
    });

export const IpxCredentialRuntimeResolveRequestSchema: z.ZodType<IpxCredentialRuntimeResolveRequest> =
    z.object({
        role_scope: IpxCredentialRuntimeRoleScopeSchema,
        definition: IpxCredentialDefinitionIdentitySchema,
        revision_hash: NonEmptyStringSchema,
        matched_url: IpxCredentialRuntimeMatchedUrlDataSchema,
        credential_uuid: z.string().uuid().optional(),
    });

const IpxCredentialRuntimeResolvedResponseSchemaBase = z
    .object({
        result: z.literal(IpxCredentialRuntimeResolveResult.RESOLVED),
        credential_uuid: z.string().uuid(),
        revision_hash: NonEmptyStringSchema,
        field_values: z.record(NonEmptyStringSchema, z.string()),
    })
    .strict();

export const IpxCredentialRuntimeResolvedResponseSchema: z.ZodType<IpxCredentialRuntimeResolvedResponse> =
    IpxCredentialRuntimeResolvedResponseSchemaBase;

const IpxCredentialRuntimeMissingCredentialResponseSchemaBase = z
    .object({
        result: z.literal(IpxCredentialRuntimeResolveResult.MISSING_CREDENTIAL),
        revision_hash: NonEmptyStringSchema,
    })
    .strict();

export const IpxCredentialRuntimeMissingCredentialResponseSchema: z.ZodType<IpxCredentialRuntimeMissingCredentialResponse> =
    IpxCredentialRuntimeMissingCredentialResponseSchemaBase;

const IpxCredentialRuntimeStaleProjectionResponseSchemaBase = z
    .object({
        result: z.literal(IpxCredentialRuntimeResolveResult.STALE_PROJECTION),
        current_revision_hash: NonEmptyStringSchema,
    })
    .strict();

export const IpxCredentialRuntimeStaleProjectionResponseSchema: z.ZodType<IpxCredentialRuntimeStaleProjectionResponse> =
    IpxCredentialRuntimeStaleProjectionResponseSchemaBase;

export const IpxCredentialRuntimeResolveResponseSchema: z.ZodType<IpxCredentialRuntimeResolveResponse> =
    z.discriminatedUnion('result', [
        IpxCredentialRuntimeResolvedResponseSchemaBase,
        IpxCredentialRuntimeMissingCredentialResponseSchemaBase,
        IpxCredentialRuntimeStaleProjectionResponseSchemaBase,
    ]);

export const RpaBuilderActiveIpxContextSchema: z.ZodType<RpaBuilderActiveIpxContext> = z.object({
    page_url: z.string().url(),
    system_id: SupportedCredentialSystemIdSchema.nullable(),
    matched_url_patterns: z.array(UrlPatternStringSchema),
});

export const RpaBuilderCapabilityFlagsSchema: z.ZodType<RpaBuilderCapabilityFlags> = z.object({
    can_use_rpa_builder: z.boolean(),
    can_manage_credential_definitions: z.boolean(),
    can_configure_credentials: z.boolean(),
});

export const RpaBuilderCredentialDefinitionProjectionSchema: z.ZodType<RpaBuilderCredentialDefinitionProjection> =
    IpxWebLoginCredentialDefinitionProjectionSchema.and(
        z.object({
            revision_hash: NonEmptyStringSchema,
            ipx_web_resource_model: z
                .lazy(() => RpaBuilderWebResourceModelProjectionSchema)
                .nullable()
                .optional(),
        })
    );

export const RpaBuilderBootstrapRequestSchema: z.ZodType<RpaBuilderBootstrapRequest> = z.object({
    active_ipx_context: RpaBuilderActiveIpxContextSchema,
});

export const RpaBuilderBootstrapResponseSchema: z.ZodType<RpaBuilderBootstrapResponse> = z.object({
    active_ipx_context: RpaBuilderActiveIpxContextSchema,
    definitions: z.array(RpaBuilderCredentialDefinitionProjectionSchema),
    capabilities: RpaBuilderCapabilityFlagsSchema,
});

function readSingleQueryValue(value: unknown): unknown {
    return Array.isArray(value) ? value[0] : value;
}

function normalizeRpaBuilderListCredentialDefinitionsRequest(value: unknown): unknown {
    const recordParseResult = z.record(z.string(), z.unknown()).safeParse(value);
    if (!recordParseResult.success) {
        return value;
    }

    const record: Record<string, unknown> = recordParseResult.data;
    const normalizedRecord: Record<string, unknown> = { ...record };
    const systemId = readSingleQueryValue(record.system_id);
    const systemIdStringParseResult = z.string().safeParse(systemId);
    if (systemIdStringParseResult.success) {
        normalizedRecord.system_id = Number(systemIdStringParseResult.data);
    }
    const credentialDefinitionKey = readSingleQueryValue(record.credential_definition_key);
    if (credentialDefinitionKey !== undefined) {
        normalizedRecord.credential_definition_key = credentialDefinitionKey;
    }
    const status = readSingleQueryValue(record.status);
    if (status !== undefined) {
        normalizedRecord.status = status;
    }

    return normalizedRecord;
}

export const RpaBuilderListCredentialDefinitionsRequestSchemaBase: z.ZodType<RpaBuilderListCredentialDefinitionsRequest> =
    z.object({
        system_id: SupportedCredentialSystemIdSchema.optional(),
        credential_definition_key: z.string().optional(),
        status: z.enum(Status).optional(),
    });

export const RpaBuilderListCredentialDefinitionsRequestSchema: z.ZodType<RpaBuilderListCredentialDefinitionsRequest> =
    z.preprocess(
        normalizeRpaBuilderListCredentialDefinitionsRequest,
        RpaBuilderListCredentialDefinitionsRequestSchemaBase
    );

export const RpaBuilderCredentialDefinitionSharedMetadataSchema: z.ZodType<RpaBuilderCredentialDefinitionSharedMetadata> =
    z.object({
        active_policy_count: z.number().int().nonnegative(),
        has_active_policy_outside_active_organization: z.boolean(),
    });

export const RpaBuilderCredentialDefinitionListItemSchema: z.ZodType<RpaBuilderCredentialDefinitionListItem> =
    RpaBuilderCredentialDefinitionProjectionSchema;

export const RpaBuilderListCredentialDefinitionsResponseSchema: z.ZodType<RpaBuilderListCredentialDefinitionsResponse> =
    z.object({
        definitions: z.array(RpaBuilderCredentialDefinitionListItemSchema),
    });

export const RpaBuilderSaveCredentialDefinitionFieldsSchema: z.ZodType<RpaBuilderSaveCredentialDefinitionFields> =
    z.object({
        system_id: SupportedCredentialSystemIdSchema,
        credential_definition_key: z.string(),
        ipx_web_resource_model_uuid: z.string().uuid().optional(),
        trigger_url_patterns: UrlPatternStringArraySchema,
        credential_field_config: IpxCredentialFieldConfigMapSchema,
        login_workflow: IpxLoginWorkflowStepListSchema,
    });

export const RpaBuilderCreateCredentialDefinitionRequestSchema: z.ZodType<RpaBuilderCreateCredentialDefinitionRequest> =
    RpaBuilderSaveCredentialDefinitionFieldsSchema;

export const RpaBuilderUpdateCredentialDefinitionRequestSchema: z.ZodType<RpaBuilderUpdateCredentialDefinitionRequest> =
    RpaBuilderSaveCredentialDefinitionFieldsSchema.and(
        z.object({
            ipx_credential_definition_uuid: z.string().uuid(),
            expected_revision_hash: NonEmptyStringSchema,
        })
    );

export const RpaBuilderConfigureLoginWorkflowCredentialDefinitionCreateRequestSchema: z.ZodType<RpaBuilderConfigureLoginWorkflowCredentialDefinitionCreateRequest> =
    z.object({
        operation: z.literal('create'),
        system_id: SupportedCredentialSystemIdSchema,
        credential_definition_key: z.string(),
        ipx_web_resource_model_uuid: z.string().uuid().optional(),
        trigger_url_patterns: UrlPatternStringArraySchema,
        credential_field_config: IpxCredentialFieldConfigMapSchema,
        login_workflow: IpxLoginWorkflowStepListSchema,
    });

export const RpaBuilderConfigureLoginWorkflowCredentialDefinitionUpdateRequestSchema: z.ZodType<RpaBuilderConfigureLoginWorkflowCredentialDefinitionUpdateRequest> =
    z.object({
        operation: z.literal('update'),
        system_id: SupportedCredentialSystemIdSchema,
        credential_definition_key: z.string(),
        ipx_web_resource_model_uuid: z.string().uuid().optional(),
        trigger_url_patterns: UrlPatternStringArraySchema,
        credential_field_config: IpxCredentialFieldConfigMapSchema,
        login_workflow: IpxLoginWorkflowStepListSchema,
        ipx_credential_definition_uuid: z.string().uuid(),
        expected_revision_hash: NonEmptyStringSchema,
    });

export const RpaBuilderConfigureLoginWorkflowCredentialDefinitionRequestSchema: z.ZodType<RpaBuilderConfigureLoginWorkflowCredentialDefinitionRequest> =
    z.union([
        RpaBuilderConfigureLoginWorkflowCredentialDefinitionCreateRequestSchema,
        RpaBuilderConfigureLoginWorkflowCredentialDefinitionUpdateRequestSchema,
    ]);

export const RpaBuilderSaveCredentialDefinitionResponseSchema: z.ZodType<RpaBuilderSaveCredentialDefinitionResponse> =
    z.object({
        definition: IpxWebLoginCredentialDefinitionProjectionSchema,
        revision_hash: NonEmptyStringSchema,
    });

export const RpaBuilderDeleteCredentialDefinitionRequestSchema: z.ZodType<RpaBuilderDeleteCredentialDefinitionRequest> =
    z.object({
        ipx_credential_definition_uuid: z.string().uuid(),
    });

export const RpaBuilderDeleteCredentialDefinitionResponseSchema: z.ZodType<RpaBuilderDeleteCredentialDefinitionResponse> =
    z.object({
        definition: IpxWebLoginCredentialDefinitionProjectionSchema,
    });

export const RpaBuilderCredentialPolicyMutationRequestSchema: z.ZodType<RpaBuilderCredentialPolicyMutationRequest> =
    z.object({
        role_scope: IpxCredentialRuntimeRoleScopeSchema,
        ipx_credential_definition_uuid: z.string().uuid(),
    });

export const RpaBuilderAttachCredentialPolicyRequestSchema: z.ZodType<RpaBuilderAttachCredentialPolicyRequest> =
    RpaBuilderCredentialPolicyMutationRequestSchema;

export const RpaBuilderReactivateCredentialPolicyRequestSchema: z.ZodType<RpaBuilderReactivateCredentialPolicyRequest> =
    RpaBuilderCredentialPolicyMutationRequestSchema;

export const RpaBuilderDeactivateCredentialPolicyRequestSchema: z.ZodType<RpaBuilderDeactivateCredentialPolicyRequest> =
    RpaBuilderCredentialPolicyMutationRequestSchema;

export const RpaBuilderCredentialPolicyMutationResponseSchema: z.ZodType<RpaBuilderCredentialPolicyMutationResponse> =
    z.object({
        definition: IpxWebLoginCredentialDefinitionProjectionSchema,
        revision_hash: NonEmptyStringSchema,
    });

export const RpaBuilderAttachCredentialPolicyResponseSchema: z.ZodType<RpaBuilderAttachCredentialPolicyResponse> =
    RpaBuilderCredentialPolicyMutationResponseSchema;

export const RpaBuilderReactivateCredentialPolicyResponseSchema: z.ZodType<RpaBuilderReactivateCredentialPolicyResponse> =
    RpaBuilderCredentialPolicyMutationResponseSchema;

export const RpaBuilderDeactivateCredentialPolicyResponseSchema: z.ZodType<RpaBuilderDeactivateCredentialPolicyResponse> =
    RpaBuilderCredentialPolicyMutationResponseSchema;

export const RpaBuilderWebResourceModelIdentitySchema: z.ZodType<RpaBuilderWebResourceModelIdentity> =
    z.object({
        ipx_web_resource_model_uuid: z.string().uuid(),
    });

export const RpaBuilderWebResourceModelDigestSchema: z.ZodType<string> =
    IpxWebResourceModelDigestSchema;

export const RpaBuilderInvalidWebResourceModelReasonSchema: z.ZodType<RpaBuilderInvalidWebResourceModelReasonType> =
    z.enum(RpaBuilderInvalidWebResourceModelReason);

export const RpaBuilderValidWebResourceModelProjectionSchema: z.ZodType<RpaBuilderValidWebResourceModelProjection> =
    RpaBuilderWebResourceModelIdentitySchema.and(
        z.object({
            system_id: SupportedCredentialSystemIdSchema,
            initial_navigation_url: z.string().url(),
            web_resource_model: IpxWebResourceModelDocumentSchema,
            model_digest: RpaBuilderWebResourceModelDigestSchema,
            status: z.enum(Status),
        })
    );

export const RpaBuilderInvalidWebResourceModelMetadataSchema: z.ZodType<RpaBuilderInvalidWebResourceModelMetadata> =
    z.object({
        reason: RpaBuilderInvalidWebResourceModelReasonSchema,
        error_message: NonEmptyStringSchema,
    });

export const RpaBuilderInvalidWebResourceModelProjectionSchema: z.ZodType<RpaBuilderInvalidWebResourceModelProjection> =
    RpaBuilderWebResourceModelIdentitySchema.and(
        z.object({
            system_id: SupportedCredentialSystemIdSchema,
            initial_navigation_url: z.string().url(),
            status: z.enum(Status),
            invalid_web_resource_model:
                RpaBuilderInvalidWebResourceModelMetadataSchema,
        })
    );

export const RpaBuilderWebResourceModelProjectionSchema: z.ZodType<RpaBuilderWebResourceModelProjection> =
    z.union([
        RpaBuilderValidWebResourceModelProjectionSchema,
        RpaBuilderInvalidWebResourceModelProjectionSchema,
    ]);

function normalizeRpaBuilderListWebResourceModelsRequest(value: unknown): unknown {
    const recordParseResult = z.record(z.string(), z.unknown()).safeParse(value);
    if (!recordParseResult.success) {
        return value;
    }

    const record: Record<string, unknown> = recordParseResult.data;
    const normalizedRecord: Record<string, unknown> = { ...record };
    const systemId = readSingleQueryValue(record.system_id);
    const systemIdStringParseResult = z.string().safeParse(systemId);
    if (systemIdStringParseResult.success) {
        normalizedRecord.system_id = Number(systemIdStringParseResult.data);
    }

    const initialNavigationUrl = readSingleQueryValue(record.initial_navigation_url);
    if (initialNavigationUrl !== undefined) {
        normalizedRecord.initial_navigation_url = initialNavigationUrl;
    }

    const status = readSingleQueryValue(record.status);
    if (status !== undefined) {
        normalizedRecord.status = status;
    }

    return normalizedRecord;
}

export const RpaBuilderListWebResourceModelsRequestSchemaBase: z.ZodType<RpaBuilderListWebResourceModelsRequest> =
    z.object({
        system_id: SupportedCredentialSystemIdSchema.optional(),
        initial_navigation_url: z.string().url().optional(),
        status: z.enum(Status).optional(),
    });

export const RpaBuilderListWebResourceModelsRequestSchema: z.ZodType<RpaBuilderListWebResourceModelsRequest> =
    z.preprocess(
        normalizeRpaBuilderListWebResourceModelsRequest,
        RpaBuilderListWebResourceModelsRequestSchemaBase
    );

export const RpaBuilderListWebResourceModelsResponseSchema: z.ZodType<RpaBuilderListWebResourceModelsResponse> =
    z.object({
        models: z.array(RpaBuilderWebResourceModelProjectionSchema),
    });

export const RpaBuilderFetchWebResourceModelRequestSchema: z.ZodType<RpaBuilderFetchWebResourceModelRequest> =
    RpaBuilderWebResourceModelIdentitySchema;

export const RpaBuilderFetchWebResourceModelResponseSchema: z.ZodType<RpaBuilderFetchWebResourceModelResponse> =
    z.object({
        model: RpaBuilderWebResourceModelProjectionSchema,
    });

export const RpaBuilderDeleteWebResourceModelRequestSchema: z.ZodType<RpaBuilderDeleteWebResourceModelRequest> =
    RpaBuilderWebResourceModelIdentitySchema;

export const RpaBuilderDeleteWebResourceModelResponseSchema: z.ZodType<RpaBuilderDeleteWebResourceModelResponse> =
    z.object({
        model: RpaBuilderWebResourceModelProjectionSchema,
    });

export const RpaBuilderSaveWebResourceModelFieldsSchema: z.ZodType<RpaBuilderSaveWebResourceModelFields> =
    z.object({
        system_id: SupportedCredentialSystemIdSchema,
        initial_navigation_url: z.string().url(),
        web_resource_model: IpxWebResourceModelDocumentSchema,
    });

export const RpaBuilderCreateWebResourceModelRequestSchema: z.ZodType<RpaBuilderCreateWebResourceModelRequest> =
    RpaBuilderSaveWebResourceModelFieldsSchema;

export const RpaBuilderReplaceWebResourceModelRequestSchema: z.ZodType<RpaBuilderReplaceWebResourceModelRequest> =
    RpaBuilderWebResourceModelIdentitySchema.and(RpaBuilderSaveWebResourceModelFieldsSchema);

export const RpaBuilderConfigureLoginWorkflowWebResourceModelCreateRequestSchema: z.ZodType<RpaBuilderConfigureLoginWorkflowWebResourceModelCreateRequest> =
    z.object({
        operation: z.literal('create'),
        system_id: SupportedCredentialSystemIdSchema,
        initial_navigation_url: z.string().url(),
        web_resource_model: IpxWebResourceModelDocumentSchema,
    });

export const RpaBuilderConfigureLoginWorkflowWebResourceModelReplaceRequestSchema: z.ZodType<RpaBuilderConfigureLoginWorkflowWebResourceModelReplaceRequest> =
    z.object({
        operation: z.literal('replace'),
        ipx_web_resource_model_uuid: z.string().uuid(),
        system_id: SupportedCredentialSystemIdSchema,
        initial_navigation_url: z.string().url(),
        web_resource_model: IpxWebResourceModelDocumentSchema,
        expected_model_digest: RpaBuilderWebResourceModelDigestSchema,
    });

export const RpaBuilderConfigureLoginWorkflowWebResourceModelRequestSchema: z.ZodType<RpaBuilderConfigureLoginWorkflowWebResourceModelRequest> =
    z.union([
        RpaBuilderConfigureLoginWorkflowWebResourceModelCreateRequestSchema,
        RpaBuilderConfigureLoginWorkflowWebResourceModelReplaceRequestSchema,
    ]);

export const RpaBuilderConfigureLoginWorkflowSaveRequestSchema: z.ZodType<RpaBuilderConfigureLoginWorkflowSaveRequest> =
    z.object({
        credential_definition:
            RpaBuilderConfigureLoginWorkflowCredentialDefinitionRequestSchema,
        web_resource_model:
            RpaBuilderConfigureLoginWorkflowWebResourceModelRequestSchema.optional(),
    });

export const RpaBuilderConfigureLoginWorkflowSaveResponseSchema: z.ZodType<RpaBuilderConfigureLoginWorkflowSaveResponse> =
    z.object({
        credential_definition: RpaBuilderCredentialDefinitionProjectionSchema,
        web_resource_model: RpaBuilderWebResourceModelProjectionSchema.nullable(),
    });

export const RpaBuilderSaveWebResourceModelResponseSchema: z.ZodType<RpaBuilderSaveWebResourceModelResponse> =
    z.object({
        model: RpaBuilderWebResourceModelProjectionSchema,
    });

export const CredentialTypeSchema: z.ZodType<CredentialType> = z.enum(CredentialType);

export const IpxCredentialRequestFieldsSchema: z.ZodType<IpxCredentialRequestFields> = z.object({
    credential_owner_scope: CredentialOwnerScopeSchema,
    ipx_credential_definition_uuid: z.string().uuid(),
});

const AuthorizableActionSchema: z.ZodType<AuthorizableAction> = z.enum(ALL_AUTHORIZABLE_ACTIONS);

const AuthorizableActionsQuerySchema = z
    .union([AuthorizableActionSchema, z.array(AuthorizableActionSchema)])
    .optional()
    .transform((value: AuthorizableAction | AuthorizableAction[] | undefined) => {
        if (value === undefined) {
            return undefined;
        }
        return Array.isArray(value) ? value : [value];
    });

const ListSecretsForOrganizationQuerySchemaBase: z.ZodType<ListSecretsForOrganizationQueryType> =
    z.object({
        actions: AuthorizableActionsQuerySchema,
        credential_type: CredentialTypeSchema.optional(),
        terms: SearchTermsSchema,
        show_deleted: BooleanQuerySchema,
        include_descendants: IncludeDescendantsQuerySchema,
        system_ids: SupportedCredentialSystemIdsQuerySchema,
        exclude_policy_linked: BooleanQuerySchema,
        limit: AuthorizationPaginationLimitQuerySchema,
        cursor: AuthorizationOpaqueCursorQuerySchema,
    });
export const ListSecretsForOrganizationQuerySchema: z.ZodType<ListSecretsForOrganizationQueryType> =
    ListSecretsForOrganizationQuerySchemaBase;

/**
 * Organization secret (returned from API)
 */
const CredentialsSchemaBase = z.object({
    credential_id: z.number(),
    credential_uuid: z.string().uuid(),
    organization_id: z.number(),
    organization_uuid: z.string().uuid(),
    system_id: SupportedCredentialSystemIdSchema,
    account_id: z.string(),
    credential_type: CredentialTypeSchema,
    public_fields: z.record(z.string(), z.unknown()),
    masked_preview: z.string(),
    status: z.enum(Status),
    applies_to_children: z.boolean(),
});
export const CredentialsSchema: z.ZodType<Credentials> = CredentialsSchemaBase;

/**
 * Organization secret with decrypted credentials
 */
export const CredentialsWithCredentialsSchema: z.ZodType<GetSecretResponse> =
    CredentialsSchemaBase.extend({
        system_id: SupportedCredentialSystemIdSchema,
        credentials: ThirdPartyCredentialsDraftSchema,
    });

type SecretRequestIpxCredentialFieldsCarrier = {
    credential_type?: CredentialType;
    credential_owner_scope?: CredentialOwnerScopeType;
    ipx_credential_definition_uuid?: string;
};

function hasIpxCredentialRequestField(dto: SecretRequestIpxCredentialFieldsCarrier): boolean {
    return (
        dto.credential_owner_scope !== undefined || dto.ipx_credential_definition_uuid !== undefined
    );
}

function addMissingIpxCredentialRequestFieldIssues(
    dto: SecretRequestIpxCredentialFieldsCarrier,
    ctx: z.RefinementCtx
): void {
    if (dto.credential_owner_scope === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['credential_owner_scope'],
            message: 'credential_owner_scope is required for IPX credentials',
        });
    }
    if (dto.ipx_credential_definition_uuid === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['ipx_credential_definition_uuid'],
            message: 'ipx_credential_definition_uuid is required for IPX credentials',
        });
    }
}

function validateCreateSecretRequestIpxFields(
    dto: SecretRequestIpxCredentialFieldsCarrier,
    ctx: z.RefinementCtx
): void {
    if (dto.credential_type === CredentialType.IPX) {
        addMissingIpxCredentialRequestFieldIssues(dto, ctx);
        return;
    }
    if (hasIpxCredentialRequestField(dto)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['credential_type'],
            message: 'IPX credential fields require credential_type IPX',
        });
    }
}

function validateUpdateSecretRequestIpxFields(
    dto: SecretRequestIpxCredentialFieldsCarrier,
    ctx: z.RefinementCtx
): void {
    if (
        dto.credential_type !== undefined &&
        dto.credential_type !== CredentialType.IPX &&
        hasIpxCredentialRequestField(dto)
    ) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['credential_type'],
            message: 'IPX credential fields require credential_type IPX',
        });
        return;
    }
    if (dto.credential_type === CredentialType.IPX || hasIpxCredentialRequestField(dto)) {
        addMissingIpxCredentialRequestFieldIssues(dto, ctx);
    }
}

/**
 * Create secret request
 */
export const GenericCreateSecretRequestSchema: z.ZodType<CreateSecretRequest> = z.object({
    organization_uuid: z.string().uuid(),
    system_id: z.literal(GenericThirdPartySystemId),
    account_id: z.string().min(1),
    credential_type: CredentialTypeSchema,
    credentials: DraftGenericCredentialsSchema,
    credential_label: z.string().optional(),
    applies_to_children: z.boolean().optional(),
    credential_owner_scope: CredentialOwnerScopeSchema.optional(),
    ipx_credential_definition_uuid: z.string().uuid().optional(),
});

export const DefaultCredentialCreateSecretRequestSchema: z.ZodType<CreateSecretRequest> = z.object({
    organization_uuid: z.string().uuid(),
    system_id: SupportedCredentialSystemIdSchema.refine(
        (systemId): boolean => ExplicitDraftCredentialsSchemaBySystemId[systemId] === undefined,
        'System-specific credential payloads must use their explicit schema.'
    ),
    account_id: z.string().min(1),
    credential_type: CredentialTypeSchema,
    credentials: DraftGenericCredentialsSchema,
    credential_label: z.string().optional(),
    applies_to_children: z.boolean().optional(),
    credential_owner_scope: CredentialOwnerScopeSchema.optional(),
    ipx_credential_definition_uuid: z.string().uuid().optional(),
});

export const ValorCreateSecretRequestSchema: z.ZodType<CreateSecretRequest> = z.object({
    organization_uuid: z.string().uuid(),
    system_id: z.literal(ValorThirdPartySystemId),
    account_id: z.string().min(1),
    credential_type: CredentialTypeSchema,
    credentials: DraftValorCredentialsSchema,
    credential_label: z.string().optional(),
    applies_to_children: z.boolean().optional(),
    credential_owner_scope: CredentialOwnerScopeSchema.optional(),
    ipx_credential_definition_uuid: z.string().uuid().optional(),
});

const CreateSecretRequestSchemaBase = z.union([
    ValorCreateSecretRequestSchema,
    DefaultCredentialCreateSecretRequestSchema,
]);
export const CreateSecretRequestSchema: z.ZodType<CreateSecretRequest> =
    CreateSecretRequestSchemaBase.superRefine(validateCreateSecretRequestIpxFields);

/**
 * Update secret request
 */
export const UpdateSecretRequestSchema: z.ZodType<UpdateSecretRequest> = z
    .object({
        account_id: z.string().min(1).optional(),
        credential_type: CredentialTypeSchema.optional(),
        credentials: ThirdPartyCredentialsDraftSchema.optional(),
        credential_label: z.string().optional(),
        applies_to_children: z.boolean().optional(),
        credential_owner_scope: CredentialOwnerScopeSchema.optional(),
        ipx_credential_definition_uuid: z.string().uuid().optional(),
        status: z.enum(Status).optional(),
    })
    .superRefine(validateUpdateSecretRequestIpxFields);

/**
 * Create secret response
 */
export const SecretIpxCredentialPolicyLinkMetadataSchema: z.ZodType<SecretIpxCredentialPolicyLinkMetadata> =
    z
        .object({
            organization_ipx_credential_policy_uuid: z.string().uuid(),
            ipx_credential_definition_uuid: z.string().uuid(),
            policy_status: z.enum(Status),
        })
        .strict();

const CreateSecretResponseSchemaBase = z.object({
    credential_uuid: z.string().uuid(),
    organization_uuid: z.string().uuid(),
    system_id: SupportedCredentialSystemIdSchema,
    account_id: z.string().min(1),
    credential_type: CredentialTypeSchema,
    public_fields: z.record(z.string(), z.unknown()),
    masked_preview: z.string(),
    status: z.enum(Status),
    applies_to_children: z.boolean(),
    ipx_credential_policy_link:
        SecretIpxCredentialPolicyLinkMetadataSchema.nullable().optional(),
});
export const CreateSecretResponseSchema: z.ZodType<CreateSecretResponse> =
    CreateSecretResponseSchemaBase;

export const SecretMetadataResponseSchema = CreateSecretResponseSchema;

export const SecretListItemCapabilitiesSchema: z.ZodType<SecretListItemCapabilities> =
    z.object({
        can_display: z.boolean(),
        can_edit: z.boolean(),
        can_delete: z.boolean(),
    });

export const ListSecretsResponseCapabilitiesSchema: z.ZodType<ListSecretsResponseCapabilities> =
    z.object({
        can_create: z.boolean(),
        can_show_deleted: z.boolean(),
    });

export const SecretListItemUsageSummarySchema: z.ZodType<SecretListItemUsageSummary> =
    z.object({
        logical_terminal_count: z.number().int().nonnegative(),
        terminal_device_count: z.number().int().nonnegative(),
        total_count: z.number().int().nonnegative(),
        has_usages: z.boolean(),
    });

/**
 * Get secret response (with decrypted credentials)
 */
export const SecretWithCredentialsResponseSchema: z.ZodType<SecretWithCredentialsResponse> =
    CreateSecretResponseSchemaBase.extend({
        system_id: SupportedCredentialSystemIdSchema,
        credentials: ThirdPartyCredentialsDraftSchema,
    });

export const GetSecretResponseSchema: z.ZodType<GetSecretResponse> =
    SecretWithCredentialsResponseSchema;

/**
 * Update secret response
 */
export const SaveSecretResponseSchema: z.ZodType<SaveSecretResponse> =
    CreateSecretResponseSchemaBase;

export const CredentialUsageTypeSchema: z.ZodType<CredentialUsageType> = z.union([
    z.literal('logical_terminal'),
    z.literal('terminal_device'),
]);

export const LogicalTerminalUsageReferenceSchema: z.ZodObject<{
    usage_type: z.ZodLiteral<'logical_terminal'>;
    logical_terminal_uuid: z.ZodString;
}> = z.object({
    usage_type: z.literal('logical_terminal'),
    logical_terminal_uuid: z.string().uuid(),
});

export const TerminalDeviceUsageReferenceSchema: z.ZodObject<{
    usage_type: z.ZodLiteral<'terminal_device'>;
    terminal_device_uuid: z.ZodString;
}> = z.object({
    usage_type: z.literal('terminal_device'),
    terminal_device_uuid: z.string().uuid(),
});

export const CredentialUsageReferenceSchema: z.ZodType<CredentialUsageReference> =
    z.discriminatedUnion('usage_type', [
        LogicalTerminalUsageReferenceSchema,
        TerminalDeviceUsageReferenceSchema,
    ]);

export const SecretIdentityReferenceSchema: z.ZodType<SecretIdentityReference> = z.object({
    credential_id: z.number(),
    credential_uuid: z.string().uuid(),
});

export const SecretUuidReferenceSchema: z.ZodType<SecretUuidReference> = z.object({
    credential_uuid: z.string().uuid(),
});

export const SecretOrganizationReferenceSchema: z.ZodType<SecretOrganizationReference> = z.object({
    organization_id: z.number(),
    organization_uuid: z.string().uuid(),
});

export const SecretOrganizationUuidReferenceSchema: z.ZodType<SecretOrganizationUuidReference> =
    z.object({
        organization_uuid: z.string().uuid(),
    });

export const LogicalTerminalUsageListItemSchema: z.ZodObject<{
    usage_type: z.ZodLiteral<'logical_terminal'>;
    logical_terminal_uuid: z.ZodString;
    display_name: z.ZodString;
}> = LogicalTerminalUsageReferenceSchema.extend({
    display_name: z.string().min(1),
});

export const TerminalDeviceUsageListItemSchema: z.ZodObject<{
    usage_type: z.ZodLiteral<'terminal_device'>;
    terminal_device_uuid: z.ZodString;
    display_name: z.ZodString;
}> = TerminalDeviceUsageReferenceSchema.extend({
    display_name: z.string().min(1),
});

export const CredentialUsageListItemSchema: z.ZodType<CredentialUsageListItem> =
    z.discriminatedUnion('usage_type', [
        LogicalTerminalUsageListItemSchema,
        TerminalDeviceUsageListItemSchema,
    ]);

export const SecretListItemUsageDataSchema: z.ZodType<SecretListItemUsageData> =
    z.object({
        summary: SecretListItemUsageSummarySchema,
        usages: z.array(CredentialUsageListItemSchema),
    });

export const SecretListItemSchema: z.ZodType<SecretListItem> =
    CreateSecretResponseSchemaBase.extend({
        capabilities: SecretListItemCapabilitiesSchema,
        usage_data: SecretListItemUsageDataSchema,
    });

/**
 * List secrets response
 */
export const ListSecretsResponseSchema: z.ZodType<ListSecretsResponse> = z.object({
    secrets: z.array(SecretListItemSchema),
    pagination: OpaqueCursorPaginationSchema.optional(),
    capabilities: ListSecretsResponseCapabilitiesSchema,
});

export const CredentialUsagesResponseSchema: z.ZodType<CredentialUsagesResponse> = z.object({
    usages: z.array(CredentialUsageListItemSchema),
});

export const CredentialDeleteConflictResponseSchema: z.ZodType<CredentialDeleteConflictResponse> =
    z.object({
        message: z.string().min(1),
        usages: z.array(CredentialUsageListItemSchema),
    });

export const DisconnectCredentialUsagesRequestSchema: z.ZodType<DisconnectCredentialUsagesRequest> =
    z.object({
        usages: z.array(CredentialUsageReferenceSchema).min(1),
    });

export const DisconnectCredentialUsagesResponseSchema: z.ZodType<DisconnectCredentialUsagesResponse> =
    z.object({
        disconnected_count: z.number().int().min(0),
        remaining_usage_count: z.number().int().min(0),
        message: z.string().min(1),
    });

/**
 * Common param schemas
 */
export const SystemIdParamSchema: z.ZodType<number> = PositiveIntSchema;
export const CredentialsIdParamSchema: z.ZodType<number> = PositiveIntSchema;
export const CredentialUuidParamSchema: z.ZodType<string> = z.string().uuid();
export const AccountIdParamSchema: z.ZodType<string> = NonEmptyStringSchema;
